import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getPublicPlanById, getPublicPlans, updatePublicPlan, getPublicPlanByIdFromAPI, type PublicPlan } from '@data/publicPlans';
import { updatePlanStatus } from '@services/plans';
import { stylePresetsByKey } from '@data/stylePresets';
import { notifyPlanPublished } from '@data/notifications';
import { useAuth } from '@services/auth';
import { createIssue } from '@services/issues';
import { useAlertDialog } from '../../shared/AlertDialog';
import BackButton from '../../shared/BackButton';
import { buildAppUrl } from '@utils/appBase';
import { getImageUrl } from '@utils/imageUrl';
import { aiGeneratedHasDesignContent, PLAN_CARD_FALLBACK_IMAGE } from '@utils/planCoverImage';
import { generatePlanPDF } from '@utils/pdfGenerator';
import ImageModal from '../../shared/ImageModal';
import { getComponentLabel } from '@data/componentLabels';
import { generateAIPlan } from '@services/aiPlanGenerator';
import { appendPlanRevision, restorePlanRevision, updatePlanContent } from '@services/plans';
import type { AIGeneratedContent } from '@data/publicPlans';
import PlanTextEditor from '@components/plan/PlanTextEditor';
import PlanEditToolbar from '@components/plan/PlanEditToolbar';
import PlanTimelineEditor from '@components/plan/PlanTimelineEditor';
import PlanRequiredToolsList from '@components/plan/PlanRequiredToolsList';
import PlanIndicativePricingCard from '@components/plan/PlanIndicativePricingCard';
import PlanMaterialsQuantitiesCard from '@components/plan/PlanMaterialsQuantitiesCard';
import {
	inviteClientToPlan,
	getMyInviteForPlan,
	acceptPlanInviteForPlan,
	declinePlanInvite,
	getCompanyInviteForPlan,
	cancelCompanyInviteForPlan,
	type PlanInviteForSeeker,
	type CompanyPlanInviteStatus
} from '@services/planInvites';
import { getMyOffersPermission } from '@services/companies';
import GanttChart from '@components/GanttChart';
import { ganttTasksFromPlanInstructions, ganttTasksFromStructuredTimeline, generateSyntheticGanttData } from '@utils/ganttDataGenerator';
import { useTranslatedAIContent } from '../../shared/useTranslatedAIContent';
import { useI18n } from '../../i18n';
import { getSkillLabel } from '@utils/skillLabels';
import ShareVisibilitySettings from '@components/plan/ShareVisibilitySettings';
import InviteSharePanel from '@components/plan/InviteSharePanel';
import ClientVisibilityModal from '@components/plan/ClientVisibilityModal';
import {
	DEFAULT_SHARE_VISIBILITY,
	normalizeShareVisibility,
	shareVisibilityShowsPricing,
	summarizeShareVisibility,
	inviteAllowsClientVisibilityEdit,
	type ShareVisibility
} from '@utils/shareVisibility';
import PlanGenerationPuzzleAnimation from '../../shared/PlanGenerationPuzzleAnimation';

// Helper function to format instruction text with highlights
function formatInstruction(instruction: string): JSX.Element {
	// Split by colon to separate title from description
	const colonIndex = instruction.indexOf(':');
	if (colonIndex === -1) {
		// No colon, return as-is with basic formatting
		return <span>{formatText(instruction)}</span>;
	}
	
	const title = instruction.substring(0, colonIndex);
	const description = instruction.substring(colonIndex + 1).trim();
	
	return (
		<>
			<span className="fw-bold" style={{ color: '#198754' }}>{formatText(title)}:</span>
			<span> {formatText(description)}</span>
		</>
	);
}

// Helper to format text with highlights for measurements, numbers, and key terms
function formatText(text: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;
	
	// Patterns to highlight:
	// 1. Timeframes: "Days 1-2", "Day 23", "24-48 hours"
	// 2. Measurements: "150-200mm", "300mm", "2-3 bags", "10-10-10"
	// 3. Percentages: "2%", "50%"
	// 4. Material names in parentheses
	// 5. Important action verbs
	
	const patterns = [
		// Timeframes
		/(Days? \d+-\d+|Day \d+|\d+-\d+ hours?)/gi,
		// Measurements with units
		/(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*(?:mm|cm|m|kg|bags?|pcs?|m²|m³|lm|times?|weeks?|months?))/gi,
		// Percentages
		/(\d+(?:\.\d+)?%)/gi,
		// Numbers followed by "x" (dimensions like "1m x 1m")
		/(\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?)/gi,
		// NPK ratios
		/(\d+-\d+-\d+)/g,
		// Material names in parentheses (e.g., "(bark chips, compost)")
		/(\([^)]+\))/g,
	];
	
	// Find all matches
	const matches: Array<{ start: number; end: number; text: string }> = [];
	
	patterns.forEach(pattern => {
		let match;
		while ((match = pattern.exec(text)) !== null) {
			matches.push({
				start: match.index,
				end: match.index + match[0].length,
				text: match[0]
			});
		}
	});
	
	// Sort matches by position
	matches.sort((a, b) => a.start - b.start);
	
	// Remove overlapping matches (keep first)
	const nonOverlapping: Array<{ start: number; end: number; text: string }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}
	
	// Build parts
	nonOverlapping.forEach((match, idx) => {
		// Add text before match
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, match.start)}</span>);
		}
		// Determine highlight style based on content
		const isParentheses = match.text.startsWith('(');
		const isTimeframe = /Days?|hours?/i.test(match.text);
		const highlightClass = isParentheses 
			? "fw-semibold" 
			: isTimeframe 
				? "fw-bold"
				: "fw-bold";
		
		// Use green tones with different shades
		const highlightStyle: React.CSSProperties = isParentheses 
			? { color: '#198754', opacity: 0.8 } // Lighter green for parentheses
			: isTimeframe 
				? { color: '#157347' } // Darker green for timeframes
				: { color: '#198754' }; // Standard green for measurements
		
		// Add highlighted match
		parts.push(
			<span key={`highlight-${idx}`} className={highlightClass} style={highlightStyle}>
				{match.text}
			</span>
		);
		lastIndex = match.end;
	});
	
	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
	}
	
	// If no matches, return original text
	if (parts.length === 0) {
		return [<span key="full">{text}</span>];
	}
	
	return parts;
}

// Helper to format key features with highlights
function formatFeatureText(feature: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;

	// Highlight only concrete, meaningful phrases instead of arbitrary sentence starts.
	const matches: Array<{ start: number; end: number; text: string }> = [];
	const numberPattern = /(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*(?:mm|cm|m|kg|%|m²|m³|lm|pcs?|sets?))/gi;
	const gardenTermPattern =
		/\b(pergola|pavers?|planters?|lighting|bollards?|cedar|timber|grill|lawn|pathway|patio|fence|irrigation|drainage|soil|mulch|shrubs?|trees?|flowerbeds?|decking|seating)\b/gi;

	let match;
	while ((match = numberPattern.exec(feature)) !== null) {
		matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
	}
	while ((match = gardenTermPattern.exec(feature)) !== null) {
		matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
	}

	matches.sort((a, b) => a.start - b.start);
	const nonOverlapping: Array<{ start: number; end: number; text: string }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}

	nonOverlapping.forEach((match, idx) => {
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{feature.substring(lastIndex, match.start)}</span>);
		}
		parts.push(
			<span key={`highlight-${idx}`} className="fw-bold" style={{ color: '#198754' }}>
				{match.text}
			</span>
		);
		lastIndex = match.end;
	});
	
	if (lastIndex < feature.length) {
		parts.push(<span key="text-end">{feature.substring(lastIndex)}</span>);
	}
	
	if (parts.length === 0) {
		return [<span key="full">{feature}</span>];
	}
	
	return parts;
}

/** Matches server-side label from `updatePlanRestoreRevision` in `plans.php` (automatic snapshot before restore). */
function isRestoreCheckpointInstruction(s: string | undefined): boolean {
	if (!s) return false;
	return s.trim() === 'Restore checkpoint';
}

export default function PlanDetails() {
	const { planId } = useParams();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const localizeStyleLabel = (styleKey?: string) => {
		switch (styleKey) {
			case 'modern': return copy('Modern', 'Modern', 'Moderni');
			case 'mediterranean': return copy('Mediterranean', 'Mediterran', 'Mediteranski');
			case 'minimalist': return copy('Minimalist', 'Minimalistisch', 'Minimalistički');
			case 'cottage': return copy('Cottage', 'Landhaus', 'Vikendica');
			case 'japanese': return copy('Japanese Zen', 'Japanischer Zen', 'Japanski Zen');
			case 'tropical': return copy('Tropical', 'Tropisch', 'Tropski');
			case 'rustic': return copy('Rustic', 'Rustikal', 'Rustikalni');
			case 'coastal': return copy('Coastal', 'Küstenstil', 'Obalni');
			case 'scandinavian': return copy('Scandinavian', 'Skandinavisch', 'Skandinavski');
			case 'traditional': return copy('Traditional', 'Traditionell', 'Tradicionalni');
			default: return stylePresetsByKey[styleKey as keyof typeof stylePresetsByKey]?.title ?? String(styleKey || '');
		}
	};
	const localizeProjectFocusLabel = (focus?: string) => {
		switch (focus) {
			case 'planting_only':
				return copy('Planting only', 'Nur Bepflanzung', 'Samo sadnja');
			case 'construction_hardscape_only':
				return copy('Construction / hardscape only', 'Nur Bau / Hardscape', 'Samo gradnja / hardscape');
			case 'mixed_planting_construction':
				return copy('Mixed (planting + construction)', 'Gemischt (Bepflanzung + Bau)', 'Mješovito (sadnja + gradnja)');
			default:
				return focus || '';
		}
	};
	const { showAlert } = useAlertDialog();
	// For non-admins, exclude hidden plans. Admins can see hidden plans via includeHidden option
	// If planId starts with "db-", always try to load from API first to get latest data
	const loadPlan = () => {
		if (!planId) return undefined;
		// For database plans, don't load from localStorage - always load from API
		if (planId.startsWith('db-') || !isNaN(parseInt(planId, 10))) {
			return undefined; // Will be loaded from API in useEffect
		}
		return getPublicPlanById(planId, { includeHidden: user?.role === 'admin' });
	};
	const [plan, setPlan] = useState<PublicPlan | undefined>(loadPlan);
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<'conceptual' | 'detailed' | 'project' | 'questionnaire'>('conceptual');
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [imageModalIndex, setImageModalIndex] = useState(0);
	const [beforeImageDimensions, setBeforeImageDimensions] = useState<{ width: number; height: number } | null>(null);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editInstructions, setEditInstructions] = useState('');
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [viewingRevisionIndex, setViewingRevisionIndex] = useState<number | null>(null);
	const [isRestoring, setIsRestoring] = useState(false);
	const [isInvitingClient, setIsInvitingClient] = useState(false);
	const [inviteModalOpen, setInviteModalOpen] = useState(false);
	const [inviteEmail, setInviteEmail] = useState('');
	const [inviteAllowEdit, setInviteAllowEdit] = useState(true);
	const [inviteShareVisibility, setInviteShareVisibility] = useState<ShareVisibility>(DEFAULT_SHARE_VISIBILITY);
	const [inviteCode, setInviteCode] = useState<string | null>(null);
	const [inviteShareText, setInviteShareText] = useState<string | null>(null);
	const [myInviteForPlan, setMyInviteForPlan] = useState<PlanInviteForSeeker | null>(null);
	const [isResolvingInvite, setIsResolvingInvite] = useState(false);
	const [companyInviteStatus, setCompanyInviteStatus] = useState<CompanyPlanInviteStatus | null>(null);
	const [isCancellingCompanyInvite, setIsCancellingCompanyInvite] = useState(false);
	const [clientVisibilityModalOpen, setClientVisibilityModalOpen] = useState(false);
	const isGardenerMarketplaceViewer = user?.role === 'gardener';
	const plansListFallback = isGardenerMarketplaceViewer ? '/app/plans' : '/app/seeker/plans';
	const [canSendMarketplaceOffers, setCanSendMarketplaceOffers] = useState(false);
	const [isReportingUser, setIsReportingUser] = useState(false);
	const [editablePlanContent, setEditablePlanContent] = useState<AIGeneratedContent | null>(null);
	const [isSavingPlanText, setIsSavingPlanText] = useState(false);
	const [isEditingPlan, setIsEditingPlan] = useState(false);

	const isCompanyViewer = user?.role === 'company';
	const canEditCompanyPlan =
		isCompanyViewer && Boolean(editablePlanContent && plan?.databaseId && viewingRevisionIndex === null);
	const companyUserId =
		user?.id != null ? (typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10)) : null;

	const clientShareVisibility = useMemo(() => {
		if (user?.role !== 'seeker') return null;
		if (!plan?.sharedByCompanyName && !myInviteForPlan?.has_invite) return null;
		return normalizeShareVisibility(
			myInviteForPlan?.share_visibility ?? (plan as PublicPlan & { share_visibility?: ShareVisibility }).share_visibility
		);
	}, [user?.role, plan, myInviteForPlan]);

	const showPricingToClient = !clientShareVisibility || shareVisibilityShowsPricing(clientShareVisibility);
	const pricingTotalOnlyForClient = clientShareVisibility?.pricing === 'total_only';
	const showTimelineToClient = !clientShareVisibility || clientShareVisibility.timeline;
	const showInstructionsToClient = !clientShareVisibility || clientShareVisibility.instructions;
	const showTeamToClient = !clientShareVisibility || clientShareVisibility.team;
	const showProjectTabToClient =
		!clientShareVisibility || showPricingToClient || showTimelineToClient || showTeamToClient;

	// Helper function to get tab button styles
	const getTabStyle = (tab: 'conceptual' | 'detailed' | 'project' | 'questionnaire') => ({
		color: activeTab === tab ? '#10b981' : '#6c757d',
		borderBottom: activeTab === tab ? '3px solid #10b981' : '3px solid transparent',
		fontWeight: activeTab === tab ? 600 : 400,
		backgroundColor: 'transparent',
		borderTop: 'none',
		borderLeft: 'none',
		borderRight: 'none',
		transition: 'all 0.2s ease'
	});

	useEffect(() => {
		if (!planId) return;
		// Determine if we can fetch from API (database plans have revisions and latest aiGenerated)
		const dbId = planId.startsWith('db-')
			? parseInt(planId.replace('db-', ''), 10)
			: !isNaN(parseInt(planId, 10))
				? parseInt(planId, 10)
				: null;
		const apiId = dbId && !isNaN(dbId) ? (planId.startsWith('db-') ? planId : `db-${dbId}`) : null;
		// Also check: planId might be "plxxxx" but local plan has databaseId – fetch from API for latest revisions
		const localPlan = getPublicPlanById(planId, { includeHidden: user?.role === 'admin' });
		const effectiveApiId = apiId ?? (localPlan?.databaseId ? `db-${localPlan.databaseId}` : null);

		if (effectiveApiId) {
			setIsLoading(true);
			getPublicPlanByIdFromAPI(effectiveApiId)
				.then(apiPlan => {
					if (apiPlan) {
						setPlan(apiPlan);
					}
					setIsLoading(false);
				})
				.catch(err => {
					console.error('Failed to load plan from API:', err);
					const fallbackPlan = getPublicPlanById(planId, { includeHidden: user?.role === 'admin' });
					if (fallbackPlan) {
						setPlan(fallbackPlan);
					}
					setIsLoading(false);
				});
		} else if (localPlan) {
			// No database ID – use local plan only
			setPlan(localPlan);
		}
	}, [planId, user]);

	useEffect(() => {
		if (!user?.id || user.role !== 'seeker' || !plan?.databaseId) {
			setMyInviteForPlan(null);
			return;
		}
		const userIdNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
		if (!userIdNum || Number.isNaN(userIdNum)) {
			setMyInviteForPlan(null);
			return;
		}
		let cancelled = false;
		getMyInviteForPlan(plan.databaseId, userIdNum)
			.then((res) => {
				if (!cancelled) setMyInviteForPlan(res);
			})
			.catch(() => {
				if (!cancelled) setMyInviteForPlan(null);
			});
		return () => {
			cancelled = true;
		};
	}, [user, plan?.databaseId]);

	useEffect(() => {
		if (!clientShareVisibility) return;
		if (activeTab === 'detailed' && !showInstructionsToClient) {
			setActiveTab('conceptual');
		}
		if (activeTab === 'project' && !showProjectTabToClient) {
			setActiveTab('conceptual');
		}
	}, [activeTab, clientShareVisibility, showInstructionsToClient, showProjectTabToClient]);

	useEffect(() => {
		if (!user?.id || user.role !== 'gardener') {
			setCanSendMarketplaceOffers(false);
			return;
		}
		let cancelled = false;
		getMyOffersPermission(user.id)
			.then((r) => {
				if (!cancelled) setCanSendMarketplaceOffers(r.can_send_marketplace_offers);
			})
			.catch(() => {
				if (!cancelled) setCanSendMarketplaceOffers(false);
			});
		return () => {
			cancelled = true;
		};
	}, [user?.id, user?.role]);

	useEffect(() => {
		if (!user?.id || user.role !== 'company' || !plan?.databaseId) {
			setCompanyInviteStatus(null);
			return;
		}
		const userIdNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
		if (!userIdNum || Number.isNaN(userIdNum)) {
			setCompanyInviteStatus(null);
			return;
		}
		let cancelled = false;
		getCompanyInviteForPlan(plan.databaseId, userIdNum)
			.then((res) => {
				if (!cancelled) setCompanyInviteStatus(res);
			})
			.catch(() => {
				if (!cancelled) setCompanyInviteStatus(null);
			});
		return () => {
			cancelled = true;
		};
	}, [user, plan?.databaseId]);

	// When viewing a revision, show that revision's content (merge with current so legacy image-only revisions still show plan text)
	const baseDisplayedAiContent = (viewingRevisionIndex != null && plan?.revisions?.[viewingRevisionIndex])
		? { ...plan.aiGenerated, ...plan.revisions[viewingRevisionIndex] }
		: plan?.aiGenerated;
	const { translatedContent: displayedAiContent } = useTranslatedAIContent(baseDisplayedAiContent ?? null);

	useEffect(() => {
		if (plan?.aiGenerated) {
			setEditablePlanContent(plan.aiGenerated);
		}
	}, [plan?.aiGenerated]);

	async function handleSaveCompanyPlanText() {
		if (!editablePlanContent || !plan?.databaseId) return;
		setIsSavingPlanText(true);
		try {
			await updatePlanContent(plan.databaseId, {
				ai_plan_details: editablePlanContent,
				after_image_url: editablePlanContent.photoAfterUrl || undefined
			});
			const updated = await getPublicPlanByIdFromAPI(plan.id);
			if (updated) setPlan(updated);
			showAlert(
				copy(
					'Plan updated. Clients see this in the shared plan; quotes keep their own copy from the quote builder.',
					'Plan aktualisiert. Kunden sehen dies im geteilten Plan; Angebote behalten ihre Kopie aus der Angebotserstellung.',
					'Plan je ažuriran. Klijenti to vide u dijeljenom planu; ponude zadržavaju kopiju iz izrade ponude.'
				),
				{ title: copy('Saved', 'Gespeichert', 'Spremljeno') }
			);
			setIsEditingPlan(false);
		} catch (err) {
			showAlert(err instanceof Error ? err.message : copy('Could not save changes.', 'Änderungen konnten nicht gespeichert werden.', 'Promjene nisu mogle biti spremljene.'), {
				title: copy('Save failed', 'Speichern fehlgeschlagen', 'Spremanje nije uspjelo')
			});
		} finally {
			setIsSavingPlanText(false);
		}
	}
	function handleDiscardCompanyPlanChanges() {
		setEditablePlanContent(plan?.aiGenerated ?? null);
		setIsEditingPlan(false);
	}

	const estimatedTimelineTasks = useMemo(() => {
		if (!plan?.aiGenerated) return [];
		const instructions = displayedAiContent?.instructions ?? plan.aiGenerated.instructions ?? [];
		const structuredTimelineTasks = displayedAiContent?.timeline_tasks ?? (plan.aiGenerated as any)?.timeline_tasks ?? [];

		const startDate = plan.questionnaire?.projectStartDate
			? new Date(plan.questionnaire.projectStartDate)
			: plan.createdAt
				? new Date(plan.createdAt)
				: new Date();

		const structured = ganttTasksFromStructuredTimeline(structuredTimelineTasks, startDate);
		if (structured.length > 0) return structured;

		if (!instructions.length) return [];

		const planBased = ganttTasksFromPlanInstructions(instructions, startDate);
		if (planBased.length > 0) return planBased;

		const budgetMid = displayedAiContent?.pricingBreakdown?.mid ?? plan.aiGenerated.pricingBreakdown?.mid ?? 0;
		return generateSyntheticGanttData('garden', budgetMid, startDate, undefined, undefined, language);
	}, [plan, displayedAiContent, language]);

	const localizedEstimatedTimelineTasks = useMemo(() => {
		const localizeTaskName = (name: string) => {
			const key = String(name || '').trim().toLowerCase();
			const labels: Record<string, { en: string; de: string; hr: string }> = {
				'site preparation': { en: 'Site Preparation', de: 'Baustellenvorbereitung', hr: 'Priprema gradilišta' },
				'material delivery': { en: 'Material Delivery', de: 'Materiallieferung', hr: 'Dostava materijala' },
				hardscaping: { en: 'Hardscaping', de: 'Bauarbeiten', hr: 'Građevinski radovi' },
				'planting - trees': { en: 'Planting - Trees', de: 'Pflanzung - Bäume', hr: 'Sadnja - Drveće' },
				'planting - shrubs': { en: 'Planting - Shrubs', de: 'Pflanzung - Sträucher', hr: 'Sadnja - Grmlje' },
				'planting - ground cover': { en: 'Planting - Ground Cover', de: 'Pflanzung - Bodendecker', hr: 'Sadnja - Pokrivači tla' },
				'irrigation setup': { en: 'Irrigation Setup', de: 'Installation der Bewässerung', hr: 'Postavljanje navodnjavanja' },
				'final touches': { en: 'Final Touches', de: 'Abschlussarbeiten', hr: 'Završni radovi' }
			};
			const mapped = labels[key];
			if (!mapped) return name;
			return language === 'de' ? mapped.de : language === 'hr' ? mapped.hr : mapped.en;
		};
		return estimatedTimelineTasks.map((task) => ({ ...task, name: localizeTaskName(task.name) }));
	}, [estimatedTimelineTasks, language]);

	useEffect(() => {
		// Reload plan when plans are updated (e.g. after moderation change)
		const handleUpdate = () => {
			if (!planId) return;
			const local = getPublicPlanById(planId, { includeHidden: user?.role === 'admin' });
			const apiId = planId.startsWith('db-') ? planId : (local?.databaseId ? `db-${local.databaseId}` : null);
			// Prefer API for database plans so we get latest revisions
			if (apiId) {
				getPublicPlanByIdFromAPI(apiId)
					.then(apiPlan => { if (apiPlan) setPlan(apiPlan); })
					.catch(() => { if (local) setPlan(local); });
			} else if (local) {
				setPlan(local);
			}
		};

		window.addEventListener('publicPlansUpdated', handleUpdate);
		return () => {
			window.removeEventListener('publicPlansUpdated', handleUpdate);
		};
	}, [planId, user]);

	if (isLoading) {
		return (
			<>
				<BackButton fallbackPath={plansListFallback} size="sm" />
				<div className="text-center py-5">
					<div className="spinner-border text-success" role="status">
						<span className="visually-hidden">{copy('Loading...', 'Wird geladen...', 'Učitavanje...')}</span>
					</div>
					<p className="text-muted mt-3">{copy('Loading plan...', 'Plan wird geladen...', 'Učitavanje plana...')}</p>
				</div>
			</>
		);
	}

	if (!plan) {
		return (
			<>
				<BackButton fallbackPath={plansListFallback} size="sm" />
				<div className="alert alert-warning mt-3">
					{copy('This plan is unavailable. It may have been deleted or you may not have permission to view it.', 'Dieser Plan ist nicht verfügbar. Er wurde möglicherweise gelöscht oder Sie haben keine Berechtigung zur Ansicht.', 'Ovaj plan nije dostupan. Možda je izbrisan ili nemate dozvolu za pregled.')}
				</div>
			</>
		);
	}

	const hasAIContent = plan.aiGenerated && plan.questionnaire;
	const style = stylePresetsByKey[plan.styleKey];
	const displayedPlanAi = displayedAiContent ?? plan.aiGenerated;
	const detailedTabTools =
		canEditCompanyPlan && editablePlanContent?.tools && Array.isArray(editablePlanContent.tools)
			? editablePlanContent.tools
			: displayedPlanAi?.tools ?? [];
	const detailedTabMaterials =
		canEditCompanyPlan &&
		editablePlanContent?.pricingBreakdown?.materials &&
		Array.isArray(editablePlanContent.pricingBreakdown.materials)
			? editablePlanContent.pricingBreakdown.materials
			: displayedPlanAi?.pricingBreakdown?.materials ?? [];
	const hasTeamAndSkills =
		(displayedPlanAi?.recommended_team_size != null && displayedPlanAi.recommended_team_size >= 1) ||
		(Array.isArray(displayedPlanAi?.required_skills) && displayedPlanAi.required_skills.length > 0);
	const regenerateAnimationBackgroundUrl =
		plan.aiGenerated?.photoAfterUrl || plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl || undefined;

	async function handleDownloadPDF() {
		if (!plan || !hasAIContent || !plan.aiGenerated || !plan.questionnaire) {
			showAlert('This plan does not have complete data for PDF generation.', { title: 'Error' });
			return;
		}

		try {
			await generatePlanPDF({
				plan,
				aiContent: plan.aiGenerated,
				questionnaire: plan.questionnaire,
				language
			});
		} catch (error) {
			console.error('Failed to generate PDF:', error);
			showAlert('Failed to generate PDF. Please try again.', { title: 'Error' });
		}
	}

	async function handleRegenerateWithChanges() {
		if (!plan || !plan.questionnaire || !planId) return;
		const originalBeforeUrl = plan.questionnaire.photoBeforeUrl || plan.photoBeforeUrl;
		const latestAfterUrl = plan.aiGenerated?.photoAfterUrl;
		if (!originalBeforeUrl) {
			showAlert('This plan has no before photo; edit the plan from My Plans to change it.', { title: 'Cannot regenerate' });
			return;
		}
		const instructions = editInstructions.trim();
		if (!instructions) {
			showAlert('Please describe what you\'d like to change (e.g. different fence colour, more flowers).', { title: 'Add your changes' });
			return;
		}
		setIsRegenerating(true);
		try {
			const questionnaire = {
				...plan.questionnaire,
				photoBeforeUrl: originalBeforeUrl
			};
			const aiContent = await generateAIPlan(questionnaire, {
				editInstructions: instructions,
				editBaseImageUrl: latestAfterUrl || originalBeforeUrl,
				originalBeforeImageUrl: originalBeforeUrl,
				existingPlan: plan.aiGenerated
			});
			const newContent = {
				...aiContent,
				photoAfterUrl: aiContent.photoAfterUrl || plan.aiGenerated?.photoAfterUrl || ''
			};
			const newRevision = { ...newContent, editInstructions: instructions };

			if (plan.databaseId) {
				await appendPlanRevision(plan.databaseId, newRevision);
				const updated = await getPublicPlanByIdFromAPI(planId);
				if (updated) setPlan(updated);
			} else {
				const existingRevisions = plan.revisions ?? [];
				const capped = existingRevisions.length >= 50 ? existingRevisions.slice(-49) : existingRevisions;
				updatePublicPlan(planId, {
					aiGenerated: newContent,
					revisions: [...capped, newRevision]
				});
				setPlan(getPublicPlanById(planId, { includeHidden: user?.role === 'admin' }));
			}
			setViewingRevisionIndex(null);
			setEditModalOpen(false);
			setEditInstructions('');
			showAlert('Plan updated with your changes.', { title: 'Done' });
		} catch (err) {
			console.error('Regenerate failed:', err);
			showAlert(err instanceof Error ? err.message : 'Failed to regenerate. Please try again.', { title: 'Error' });
		} finally {
			setIsRegenerating(false);
		}
	}

	async function handleRestoreRevision(revisionIndex: number) {
		if (!plan || !planId) return;
		setIsRestoring(true);
		try {
			if (plan.databaseId) {
				await restorePlanRevision(plan.databaseId, revisionIndex);
				const updated = await getPublicPlanByIdFromAPI(planId);
				if (updated) setPlan(updated);
			} else {
				const rev = plan.revisions?.[revisionIndex];
				if (rev) {
					updatePublicPlan(planId, {
						aiGenerated: {
							photoAfterUrl: rev.photoAfterUrl,
							planDescription: rev.planDescription,
							keyFeatures: rev.keyFeatures,
							instructions: rev.instructions,
							timeline_tasks: rev.timeline_tasks,
							tools: rev.tools,
							pricingBreakdown: rev.pricingBreakdown,
						required_skills: rev.required_skills,
						recommended_team_size: rev.recommended_team_size,
							generatedAt: rev.generatedAt
						}
					});
					setPlan(getPublicPlanById(planId, { includeHidden: user?.role === 'admin' }));
				}
			}
			setViewingRevisionIndex(null);
			showAlert(copy('This version is now your main design.', 'Diese Version ist jetzt Ihr Hauptentwurf.', 'Ova verzija je sada vaš glavni dizajn.'), { title: copy('Restored', 'Wiederhergestellt', 'Vraćeno') });
		} catch (err) {
			console.error('Restore failed:', err);
			showAlert(err instanceof Error ? err.message : 'Failed to restore. Please try again.', { title: 'Error' });
		} finally {
			setIsRestoring(false);
		}
	}

	async function handleReportSeekerFromPlan() {
		const currentPlan = plan;
		if (!currentPlan) return;
		const reporterUserId = user?.id ? parseInt(String(user.id), 10) : 0;
		const targetUserId = currentPlan.seekerUserId ? parseInt(String(currentPlan.seekerUserId), 10) : 0;
		if (!reporterUserId || Number.isNaN(reporterUserId)) {
			window.alert(copy('Please log in to report users.', 'Bitte melden Sie sich an, um Nutzer zu melden.', 'Prijavite se kako biste prijavili korisnike.'));
			return;
		}
		if (!targetUserId || Number.isNaN(targetUserId)) {
			window.alert(copy('This plan cannot be reported yet.', 'Dieser Plan kann derzeit nicht gemeldet werden.', 'Ovaj plan se trenutno ne može prijaviti.'));
			return;
		}
		const details = window.prompt(
			copy(
				'Optional: tell us why you are reporting this user.',
				'Optional: Warum melden Sie diesen Nutzer?',
				'Neobavezno: recite zašto prijavljujete ovog korisnika.'
			),
			''
		);
		setIsReportingUser(true);
		try {
			await createIssue({
				reporter_user_id: reporterUserId,
				type: 'user_report',
				category: 'user',
				target_type: 'user',
				target_id: targetUserId,
				reason_code: 'other',
				message: details?.trim() || `Reported from plan ${currentPlan.title}`
			});
			window.alert(copy('Report submitted. Thank you.', 'Meldung gesendet. Danke.', 'Prijava je poslana. Hvala.'));
		} catch (err: any) {
			window.alert(err?.message || copy('Failed to submit report.', 'Meldung konnte nicht gesendet werden.', 'Slanje prijave nije uspjelo.'));
		} finally {
			setIsReportingUser(false);
		}
	}

	return (
		<>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
				<div className="d-flex align-items-center gap-2">
					<BackButton fallbackPath={plansListFallback} label={copy('Back to plans', 'Zurück zu Plänen', 'Natrag na planove')} size="sm" />
					<h1 className="h3 mb-0">{plan.title}</h1>
				</div>
				<div className="d-flex align-items-center gap-2">
					{!isGardenerMarketplaceViewer && hasAIContent && (plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl) && (
						<button
							type="button"
							className="btn btn-outline-success btn-sm"
							onClick={() => setEditModalOpen(true)}
							disabled={isRegenerating}
						>
							<i className="bi bi-pencil-square me-1"></i>{copy('Change something?', 'Etwas ändern?', 'Promijeniti nešto?')}
						</button>
					)}
					{plan.status === 'draft' && <span className="badge bg-secondary">{copy('Draft', 'Entwurf', 'Skica')}</span>}
					{plan.status === 'active' && <span className="badge bg-success">{copy('Active', 'Aktiv', 'Aktivno')}</span>}
					{plan.status === 'closed' && <span className="badge bg-secondary">{copy('Closed', 'Geschlossen', 'Zatvoreno')}</span>}
				</div>
			</div>

			{viewingRevisionIndex != null && (
				<div className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
					<span>{copy('Viewing a previous version.', 'Sie sehen eine frühere Version.', 'Gledate prethodnu verziju.')}</span>
					<div className="d-flex gap-2">
						<button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setViewingRevisionIndex(null)}>
							{copy('View main design', 'Hauptentwurf anzeigen', 'Prikaži glavni dizajn')}
						</button>
						{!isGardenerMarketplaceViewer && (
							<button type="button" className="btn btn-sm btn-success" onClick={() => handleRestoreRevision(viewingRevisionIndex)} disabled={isRestoring}>
								{isRestoring ? <span className="spinner-border spinner-border-sm me-1" /> : null}
								{copy('Restore this version', 'Diese Version wiederherstellen', 'Vrati ovu verziju')}
							</button>
						)}
					</div>
				</div>
			)}

			{hasAIContent ? (
				// Show AI-generated plan content
				<>
					{/* Before/After Comparison */}
					<div className="card mb-4">
						<div className="card-header">
							<h5 className="mb-0">{copy('Before & After', 'Vorher & Nachher', 'Prije i poslije')}</h5>
						</div>
						<div className="card-body">
							<div className="row g-3">
								<div className="col-12 col-md-6">
								<div className="text-muted small mb-2">{copy('Current State', 'Aktueller Zustand', 'Trenutačno stanje')}</div>
								{(() => {
									// ALWAYS use the uploaded photo from before_image_url
									// Check both questionnaire and plan level for the before image URL
									const beforeImageUrl = plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl;

									if (beforeImageUrl) {
										const imageSrc = getImageUrl(beforeImageUrl) || beforeImageUrl;
										return (
											<>
												<div
													className="before-after-image-container"
													style={{
														width: '100%',
														height: '400px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														backgroundColor: '#f8f9fa',
														borderRadius: '0.375rem',
														cursor: 'pointer',
														overflow: 'hidden',
														border: '1px solid #dee2e6'
													}}
													onClick={() => {
														const afterImageUrl = displayedAiContent?.photoAfterUrl;
														const isPlaceholder = afterImageUrl?.includes('unsplash.com');
														const hasAfterImage = afterImageUrl && !isPlaceholder;
														setImageModalIndex(0);
														setImageModalOpen(true);
													}}
												>
													<img
														src={imageSrc}
														alt={copy('Before', 'Vorher', 'Prije')}
														className="img-fluid rounded"
														style={{
															width: '100%',
															height: '100%',
															objectFit: 'contain',
															pointerEvents: 'none'
														}}
														onLoad={(e) => {
															const img = e.currentTarget;
															if (img.naturalWidth && img.naturalHeight) {
																setBeforeImageDimensions({
																	width: img.naturalWidth,
																	height: img.naturalHeight
																});
															}
														}}
														onError={(e) => {
															console.error('❌ Failed to load before image:', beforeImageUrl);
															console.error('❌ Image src was:', imageSrc);
														}}
													/>
												</div>
												<small className="text-muted d-block mt-2">
													<i className="bi bi-zoom-in me-1"></i>{copy('Click to expand', 'Klicken zum Vergrößern', 'Kliknite za proširenje')}
												</small>
											</>
										);
									}
									
									// No image available
									return (
										<div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
											<span className="text-muted">{copy('No photo provided', 'Kein Foto vorhanden', 'Nema fotografije')}</span>
										</div>
									);
								})()}
								</div>
								<div className="col-12 col-md-6">
									<div className="text-muted small mb-2">{copy('Design Vision', 'Designvision', 'Dizajnerska vizija')}</div>
									{(() => {
										// Use the AI-generated after image if available (or the revision being viewed)
										const afterImageUrl = displayedAiContent?.photoAfterUrl;
										const beforeImageUrl = plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl;

										// Check if photoAfterUrl is a placeholder (Unsplash) or a real AI-generated image
										const isPlaceholderImage = afterImageUrl?.includes('unsplash.com');
										const agForAfterPanel = displayedAiContent ?? plan.aiGenerated;
										const showRealAfterPanel =
											Boolean(afterImageUrl) &&
											!isPlaceholderImage &&
											(plan.status !== 'draft' || aiGeneratedHasDesignContent(agForAfterPanel));

										// If we have a real AI-generated image (not a placeholder), use it
										if (showRealAfterPanel && afterImageUrl) {
											const imageSrc = getImageUrl(afterImageUrl);
											// Use the same aspect ratio as the before image if available
											return (
												<>
													<div
														className="before-after-image-container"
														style={{
															width: '100%',
															height: '400px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															backgroundColor: '#f8f9fa',
															borderRadius: '0.375rem',
															cursor: 'pointer',
															overflow: 'hidden',
															border: '2px solid #198754'
														}}
														onClick={() => {
															setImageModalIndex(1);
															setImageModalOpen(true);
														}}
													>
															<img
															src={imageSrc}
															alt={copy('After design', 'Nach dem Design', 'Nakon dizajna')}
															className="img-fluid rounded"
															style={{
																width: '100%',
																height: '100%',
																objectFit: 'contain',
																pointerEvents: 'none'
															}}
															onError={(e) => {
																console.error('❌ Failed to load after image:', afterImageUrl);
																console.error('❌ Image src was:', imageSrc);
															}}
														/>
													</div>
													<small className="text-muted d-block mt-2">
														<i className="bi bi-zoom-in me-1"></i>{copy('Click to expand', 'Klicken zum Vergrößern', 'Kliknite za proširenje')}
													</small>
												</>
											);
										}
										
										// Fallback to before image if no real AI-generated image is available
										if (beforeImageUrl) {
											const imageSrc = getImageUrl(beforeImageUrl) || beforeImageUrl;
											return (
												<>
													<div
														style={{
															width: '100%',
															height: '400px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															backgroundColor: '#f8f9fa',
															borderRadius: '0.375rem',
															cursor: 'pointer',
															overflow: 'hidden',
															border: '2px solid #198754'
														}}
														onClick={() => {
															setImageModalIndex(0);
															setImageModalOpen(true);
														}}
													>
															<img
															src={imageSrc}
															alt={copy('After design (same as before photo)', 'Nach dem Design (gleiches Foto wie vorher)', 'Nakon dizajna (isto kao fotografija prije)')}
															className="img-fluid rounded"
															style={{
																maxWidth: '100%',
																maxHeight: '100%',
																objectFit: 'contain',
																pointerEvents: 'none'
															}}
															onError={(e) => {
																console.error('❌ Failed to load after image:', beforeImageUrl);
																console.error('❌ Image src was:', imageSrc);
															}}
														/>
													</div>
													<small className="text-muted d-block mt-2">
														<i className="bi bi-zoom-in me-1"></i>{copy('Click to expand', 'Klicken zum Vergrößern', 'Kliknite za proširenje')}
													</small>
												</>
											);
										}
										
										// No image available
										return (
											<div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
												<span className="text-muted">{copy('No photo provided', 'Kein Foto vorhanden', 'Nema fotografije')}</span>
											</div>
										);
									})()}
								</div>
							</div>
						</div>
					</div>

					{/* History: versions in chronological order */}
					{plan.revisions && plan.revisions.length > 0 && (
						<div className="card mb-4">
							<div className="card-header d-flex align-items-center gap-2 flex-wrap">
								<h5 className="mb-0">{copy('Versions', 'Versionen', 'Verzije')}</h5>
								<button
									type="button"
									className="btn btn-link btn-sm text-muted p-0 border-0 align-baseline"
									style={{ lineHeight: 1 }}
									title={copy(
										'Oldest on the left, main design on the right. Click a card for notes and full text. The last card is your main design — it is what appears on this plan and to others when published.',
										'Links die ältesten, rechts Ihr Hauptentwurf. Klicken Sie eine Karte für Hinweise und vollen Text. Die letzte Karte ist Ihr Hauptentwurf — sichtbar auf diesem Plan und für andere bei Veröffentlichung.',
										'S lijeva najstarije, s desna glavni dizajn. Kliknite karticu za napomene i cijeli tekst. Zadnja kartica je glavni dizajn — vidljiv na ovom planu i drugima kad je objavljen.'
									)}
									aria-label={copy('How versions work', 'So funktionieren Versionen', 'Kako verzije rade')}
								>
									<i className="bi bi-info-circle" aria-hidden="true" />
								</button>
							</div>
							<div className="card-body">
								<div className="d-flex flex-wrap gap-3">
									{/* Revisions (oldest first), then main design as latest */}
									{(plan.revisions ?? []).map((rev, actualIndex) => {
										const isViewing = viewingRevisionIndex === actualIndex;
										const checkpoint = isRestoreCheckpointInstruction(rev.editInstructions);
										return (
											<div
												key={actualIndex}
												className="border rounded p-2 text-center"
												style={{ width: 120, cursor: 'pointer', borderWidth: isViewing ? 2 : 1, borderColor: isViewing ? '#10b981' : '#dee2e6' }}
												onClick={() => setViewingRevisionIndex(actualIndex)}
											>
												{rev.photoAfterUrl && !rev.photoAfterUrl.includes('unsplash.com') ? (
													<img src={getImageUrl(rev.photoAfterUrl)} alt={`Version ${actualIndex + 1}`} className="rounded" style={{ width: '100%', height: 80, objectFit: 'cover' }} />
												) : (
													<div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: 80 }}><span className="text-muted small">v{actualIndex + 1}</span></div>
												)}
												<div className="small mt-1">{rev.generatedAt ? new Date(rev.generatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : `Version ${actualIndex + 1}`}</div>
												{checkpoint ? (
													<div className="small mt-1">
														<span className="badge rounded-pill text-bg-light border text-wrap fw-normal" style={{ fontSize: '0.65rem', maxWidth: '100%' }}>
															{copy('Previous design kept', 'Vorheriger Entwurf gesichert', 'Spremljen prethodni dizajn')}
														</span>
													</div>
												) : rev.editInstructions?.trim() ? (
													<div
														className="small text-muted mt-1 px-0 text-start"
														style={{
															display: '-webkit-box',
															WebkitLineClamp: 2,
															WebkitBoxOrient: 'vertical',
															overflow: 'hidden',
															wordBreak: 'break-word',
															lineHeight: 1.2
														}}
														title={rev.editInstructions}
													>
														{rev.editInstructions}
													</div>
												) : null}
												{!isGardenerMarketplaceViewer && (
													<button type="button" className="btn btn-sm btn-outline-success mt-1" onClick={(e) => { e.stopPropagation(); handleRestoreRevision(actualIndex); }} disabled={isRestoring}>
														{copy('Restore', 'Wiederherstellen', 'Vrati')}
													</button>
												)}
											</div>
										);
									})}
									<div
										className="border rounded p-2 text-center"
										style={{ width: 120, cursor: 'pointer', borderWidth: viewingRevisionIndex === null ? 2 : 1, borderColor: viewingRevisionIndex === null ? '#10b981' : '#dee2e6' }}
										onClick={() => setViewingRevisionIndex(null)}
									>
										{plan.aiGenerated?.photoAfterUrl &&
										!plan.aiGenerated.photoAfterUrl.includes('unsplash.com') &&
										(plan.status !== 'draft' || aiGeneratedHasDesignContent(plan.aiGenerated)) ? (
											<img src={getImageUrl(plan.aiGenerated.photoAfterUrl)} alt={copy('Main design', 'Hauptentwurf', 'Glavni dizajn')} className="rounded" style={{ width: '100%', height: 80, objectFit: 'cover' }} />
										) : (
											<div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: 80 }}><span className="text-muted small">{copy('Main', 'Haupt', 'Glavni')}</span></div>
										)}
										<div className="small mt-1 fw-semibold">{copy('Main', 'Haupt', 'Glavni')}</div>
									</div>
								</div>
								{viewingRevisionIndex != null && (() => {
									const rev = plan.revisions?.[viewingRevisionIndex];
									if (!rev) return null;
									if (isRestoreCheckpointInstruction(rev.editInstructions)) {
										return (
											<div className="mt-3 p-3 rounded border bg-light">
												<div className="small text-muted text-uppercase fw-semibold mb-2" style={{ letterSpacing: '0.03em' }}>
													{copy('Previous design', 'Vorheriger Entwurf', 'Prethodni dizajn')}
												</div>
												<p className="small mb-0 text-body-secondary">
													{copy(
														'When you chose "Restore" on an older version, your plan switched to that look. This entry is simply the design you had right before that switch — kept so you can preview it again or restore it if you prefer it.',
														'Als Sie bei einer älteren Version „Wiederherstellen“ gewählt haben, wechselte Ihr Plan zu diesem Look. Dieser Eintrag ist der Entwurf, den Sie unmittelbar davor hatten — gespeichert, damit Sie ihn erneut ansehen oder bei Bedarf wiederherstellen können.',
														'Kad ste na starijoj verziji odabrali „Vrati“, plan se prebacio na taj izgled. Ovaj zapis je dizajn koji ste imali neposredno prije tog koraka — ostavljen da ga ponovno pogledate ili ga vratite ako vam više odgovara.'
													)}
												</p>
											</div>
										);
									}
									const prompt = rev.editInstructions?.trim();
									if (!prompt) return null;
									return (
										<div className="mt-3 p-3 rounded border">
											<div className="small text-muted text-uppercase fw-semibold mb-2" style={{ letterSpacing: '0.03em' }}>
												{copy('What you asked to change', 'Ihre Änderungswünsche', 'Što ste tražili promijeniti')}
											</div>
											<p className="mb-0 small text-body" style={{ whiteSpace: 'pre-wrap' }}>
												{prompt}
											</p>
										</div>
									);
								})()}
							</div>
						</div>
					)}

					{/* Tabs Navigation */}
					<div className="card mb-4">
						<div className="card-body p-0">
							{canEditCompanyPlan && editablePlanContent && (
								<div className="px-3 pt-3 pb-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
									<p className="text-muted small mb-0">
										{copy(
											'Edit any tab while editing is on, then save once.',
											'Bearbeiten Sie beliebige Tabs im Bearbeitungsmodus und speichern Sie einmal.',
											'Uredite bilo koju karticu dok je uređivanje uključeno, zatim jednom spremite.'
										)}
									</p>
									<PlanEditToolbar
										copy={copy}
										isEditing={isEditingPlan}
										onToggleEdit={() => setIsEditingPlan((v) => !v)}
										onDiscard={handleDiscardCompanyPlanChanges}
										onSave={handleSaveCompanyPlanText}
										isSaving={isSavingPlanText}
									/>
								</div>
							)}
							<ul className="nav nav-tabs" role="tablist" style={{ borderBottomColor: '#a7f3d0', borderBottomWidth: '2px' }}>
								<li className="nav-item" role="presentation">
									<button
										className={`nav-link ${activeTab === 'conceptual' ? 'active' : ''}`}
										onClick={() => setActiveTab('conceptual')}
										type="button"
										role="tab"
										style={getTabStyle('conceptual')}
										onMouseEnter={(e) => {
											if (activeTab !== 'conceptual') {
												e.currentTarget.style.color = '#10b981';
												e.currentTarget.style.borderBottomColor = '#a7f3d0';
											}
										}}
										onMouseLeave={(e) => {
											if (activeTab !== 'conceptual') {
												e.currentTarget.style.color = '#6c757d';
												e.currentTarget.style.borderBottomColor = 'transparent';
											}
										}}
									>
										{copy('Conceptual Plan', 'Konzeptplan', 'Konceptualni plan')}
									</button>
								</li>
								{showInstructionsToClient && (
								<li className="nav-item" role="presentation">
									<button
										className={`nav-link ${activeTab === 'detailed' ? 'active' : ''}`}
										onClick={() => setActiveTab('detailed')}
										type="button"
										role="tab"
										style={getTabStyle('detailed')}
										onMouseEnter={(e) => {
											if (activeTab !== 'detailed') {
												e.currentTarget.style.color = '#10b981';
												e.currentTarget.style.borderBottomColor = '#a7f3d0';
											}
										}}
										onMouseLeave={(e) => {
											if (activeTab !== 'detailed') {
												e.currentTarget.style.color = '#6c757d';
												e.currentTarget.style.borderBottomColor = 'transparent';
											}
										}}
									>
										{copy('Project Plan', 'Projektplan', 'Projektni plan')}
									</button>
								</li>
								)}
								{showProjectTabToClient && (
								<li className="nav-item" role="presentation">
									<button
										className={`nav-link ${activeTab === 'project' ? 'active' : ''}`}
										onClick={() => setActiveTab('project')}
										type="button"
										role="tab"
										style={getTabStyle('project')}
										onMouseEnter={(e) => {
											if (activeTab !== 'project') {
												e.currentTarget.style.color = '#10b981';
												e.currentTarget.style.borderBottomColor = '#a7f3d0';
											}
										}}
										onMouseLeave={(e) => {
											if (activeTab !== 'project') {
												e.currentTarget.style.color = '#6c757d';
												e.currentTarget.style.borderBottomColor = 'transparent';
											}
										}}
									>
										{copy('Detailed Plan', 'Detaillierter Plan', 'Detaljni plan')}
									</button>
								</li>
								)}
								<li className="nav-item" role="presentation">
									<button
										className={`nav-link ${activeTab === 'questionnaire' ? 'active' : ''}`}
										onClick={() => setActiveTab('questionnaire')}
										type="button"
										role="tab"
										style={getTabStyle('questionnaire')}
										onMouseEnter={(e) => {
											if (activeTab !== 'questionnaire') {
												e.currentTarget.style.color = '#10b981';
												e.currentTarget.style.borderBottomColor = '#a7f3d0';
											}
										}}
										onMouseLeave={(e) => {
											if (activeTab !== 'questionnaire') {
												e.currentTarget.style.color = '#6c757d';
												e.currentTarget.style.borderBottomColor = 'transparent';
											}
										}}
									>
										{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}
									</button>
								</li>
							</ul>
						</div>
					</div>

					<div className="row g-4">
						{/* Main Content Area */}
						<div className="col-12 col-lg-8">
							{/* Conceptual Plan Tab */}
							{activeTab === 'conceptual' && (
								<>
									<div className="card mb-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Plan Overview', 'Planübersicht', 'Pregled plana')}</h5>
										</div>
										<div className="card-body">
											<div className="mb-3">
												<div className="text-muted small">{copy('Style', 'Stil', 'Stil')}</div>
												<div className="fw-bold">{localizeStyleLabel(plan.styleKey)}</div>
											</div>
											<div className="mb-3">
												<div className="text-muted small">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</div>
												<div className="fw-bold">{plan.questionnaire!.gardenSize} m²</div>
											</div>
											<div className="mb-3">
												<div className="text-muted small">{copy('Location', 'Standort', 'Lokacija')}</div>
												<div className="fw-bold">{plan.city}</div>
											</div>
											{plan.questionnaire?.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
												<div className="mb-3">
													<div className="text-muted small mb-2">{copy('Garden Components', 'Gartenkomponenten', 'Komponente vrta')}</div>
													<div className="d-flex flex-wrap gap-2">
														{plan.questionnaire.selectedComponents.map((componentId) => (
															<span key={componentId} className="badge bg-success text-white">
																<i className="bi bi-check-circle me-1"></i>
																{getComponentLabel(componentId, language)}
															</span>
														))}
													</div>
												</div>
											)}
										</div>
									</div>

									{canEditCompanyPlan && editablePlanContent ? (
										<PlanTextEditor
											content={editablePlanContent}
											onContentChange={setEditablePlanContent}
											copy={copy}
											editable
											sections={['description', 'features']}
											isEditing={isEditingPlan}
											showEditControls={false}
										/>
									) : (
										<>
											<div className="card mb-4">
												<div className="card-body">
													<div className="text-muted small mb-2">{copy('Description', 'Beschreibung', 'Opis')}</div>
													<p className="mb-0">{(displayedAiContent ?? plan.aiGenerated)!.planDescription}</p>
												</div>
											</div>
											<div className="card mb-4">
												<div className="card-header">
													<h5 className="mb-0">{copy('Key Features', 'Hauptmerkmale', 'Ključne značajke')}</h5>
												</div>
												<div className="card-body">
													<ul className="list-unstyled mb-0">
														{(displayedAiContent ?? plan.aiGenerated)!.keyFeatures.map((feature, idx) => (
															<li key={idx} className="mb-3">
																<i className="bi bi-check-circle-fill text-success me-2"></i>
																{formatFeatureText(feature)}
															</li>
														))}
													</ul>
												</div>
											</div>
										</>
									)}
								</>
							)}

							{/* Detailed Plan Tab */}
							{activeTab === 'detailed' && showInstructionsToClient && (
								<>
									{canEditCompanyPlan && editablePlanContent ? (
										<PlanTextEditor
											content={editablePlanContent}
											onContentChange={setEditablePlanContent}
											copy={copy}
											editable
											sections={['instructions']}
											isEditing={isEditingPlan}
											showEditControls={false}
										/>
									) : (
									<div className="card mb-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Implementation Instructions', 'Umsetzungsanweisungen', 'Upute za izvedbu')}</h5>
										</div>
										<div className="card-body">
											<ol className="mb-0">
												{(displayedAiContent ?? plan.aiGenerated)!.instructions.map((instruction, idx) => (
													<li key={idx} className="mb-3" style={{ lineHeight: '1.8' }}>
														{formatInstruction(instruction)}
													</li>
												))}
											</ol>
										</div>
									</div>
									)}

									{/* Required Tools */}
									{detailedTabTools.length > 0 || (canEditCompanyPlan && isEditingPlan) ? (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">
													<i className="bi bi-tools me-2"></i>{copy('Required Tools', 'Benötigte Werkzeuge', 'Potrebni alati')}
												</h5>
											</div>
											<div className="card-body">
												<PlanRequiredToolsList
													tools={
														typeof detailedTabTools?.[0] === 'string'
															? (detailedTabTools as string[])
															: (detailedTabTools as Array<{
																	name: string;
																	description: string;
																}>)
													}
													editable={canEditCompanyPlan}
													isEditing={isEditingPlan}
													onChange={(nextTools) =>
														setEditablePlanContent((prev) => {
															if (!prev) return prev;
															return { ...prev, tools: nextTools as AIGeneratedContent['tools'] };
														})
													}
												/>
											</div>
										</div>
									) : null}

									{detailedTabMaterials.length > 0 || (canEditCompanyPlan && isEditingPlan) ? (
											<PlanMaterialsQuantitiesCard
												materials={detailedTabMaterials}
												copy={copy}
												editable={canEditCompanyPlan}
												isEditing={isEditingPlan}
												onChange={(nextMaterials) =>
													setEditablePlanContent((prev) => {
														if (!prev) return prev;
														return {
															...prev,
															pricingBreakdown: {
																...prev.pricingBreakdown,
																materials: nextMaterials
															}
														};
													})
												}
											/>
										) : null}
								</>
							)}

							{/* Project Plan Tab */}
							{activeTab === 'project' && (
								<>
									{!showPricingToClient ? (
										<div className="alert alert-info border-0 mb-4">
											<i className="bi bi-receipt me-2" />
											{copy(
												'Pricing for this project is in your company quote (Offers). Open Offers to review amounts, accept, or decline.',
												'Die Preise für dieses Projekt stehen in Ihrem Firmenangebot (Angebote). Öffnen Sie Angebote, um Beträge zu prüfen und anzunehmen oder abzulehnen.',
												'Cijene za ovaj projekt nalaze se u ponudi tvrtke (Ponude). Otvorite Ponude za pregled, prihvaćanje ili odbijanje.'
											)}
										</div>
									) : canEditCompanyPlan && editablePlanContent ? (
										isEditingPlan ? (
											<PlanTextEditor
												content={editablePlanContent}
												onContentChange={setEditablePlanContent}
												copy={copy}
												editable
												sections={['pricing']}
												isEditing={isEditingPlan}
												showEditControls={false}
											/>
										) : (displayedAiContent ?? plan.aiGenerated)?.pricingBreakdown ? (
											<PlanIndicativePricingCard
												pricing={(displayedAiContent ?? plan.aiGenerated)!.pricingBreakdown}
												copy={copy}
												showDisclaimer={false}
												totalOnly={pricingTotalOnlyForClient}
											/>
										) : null
									) : (displayedAiContent ?? plan.aiGenerated)?.pricingBreakdown ? (
										<PlanIndicativePricingCard
											pricing={(displayedAiContent ?? plan.aiGenerated)!.pricingBreakdown}
											copy={copy}
											showDisclaimer={false}
											totalOnly={pricingTotalOnlyForClient}
										/>
									) : null}

									{/* Timeline */}
									{showTimelineToClient && plan.questionnaire?.timeline && (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</h5>
											</div>
											<div className="card-body">
												<p className="mb-0">{plan.questionnaire.timeline}</p>
											</div>
										</div>
									)}

									{/* Preferred Start Date */}
									{showTimelineToClient && plan.questionnaire?.projectStartDate && (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">
													<i className="bi bi-calendar3 text-success me-2"></i>{copy('Preferred Start Date', 'Bevorzugtes Startdatum', 'Željeni datum početka')}
												</h5>
											</div>
											<div className="card-body">
												<p className="mb-0">
													<strong>{new Date(plan.questionnaire.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
												</p>
												<p className="text-muted small mb-0 mt-2">{copy("This is when you indicated you'd like the project to begin.", 'Dies ist der Termin, den Sie als gewünschten Projektstart angegeben haben.', 'Ovo je datum koji ste naveli kao željeni početak projekta.')}</p>
											</div>
										</div>
									)}

									{showTimelineToClient && ((canEditCompanyPlan && editablePlanContent) || estimatedTimelineTasks.length > 0) ? (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">
													<i className="bi bi-bar-chart-steps text-success me-2"></i>
													{copy('Estimated Work Plan (Weeks)', 'Geschätzter Arbeitsplan (Wochen)', 'Procijenjeni plan rada (tjedni)')}
												</h5>
											</div>
											<div className="card-body">
												{canEditCompanyPlan && editablePlanContent ? (
													<PlanTimelineEditor
														plan={plan}
														content={editablePlanContent}
														onContentChange={setEditablePlanContent}
														copy={copy}
														language={language}
														isEditing={isEditingPlan}
														companyUserId={companyUserId}
													/>
												) : (
													<>
														<p className="small text-muted mb-3">
															{copy('Rough sequence and duration preview. Final dates and assigned gardeners are set by the company in the accepted offer/project.', 'Grobe Vorschau auf Ablauf und Dauer. Endgültige Termine und zugewiesene Gärtner legt die Firma im angenommenen Angebot/Projekt fest.', 'Grubi prikaz redoslijeda i trajanja. Konačne datume i dodijeljene vrtlare postavlja tvrtka u prihvaćenoj ponudi/projektu.')}
														</p>
														<GanttChart tasks={localizedEstimatedTimelineTasks} preview />
													</>
												)}
											</div>
										</div>
									) : null}
								</>
							)}

							{activeTab === 'project' && showTeamToClient && hasTeamAndSkills && (
								<div className="card mb-4">
									<div className="card-header">
										<h5 className="mb-0">{copy('Team & skills', 'Team und Kompetenzen', 'Tim i vještine')}</h5>
									</div>
									<div className="card-body">
										{displayedPlanAi?.recommended_team_size != null && displayedPlanAi.recommended_team_size >= 1 && (
											<div className="mb-3">
												<div className="text-muted small mb-1">{copy('Recommended team size', 'Empfohlene Teamgröße', 'Preporučena veličina tima')}</div>
												<div className="fw-bold text-success">
													{displayedPlanAi.recommended_team_size}{' '}
													{displayedPlanAi.recommended_team_size === 1
														? copy('person', 'Person', 'osoba')
														: copy('people', 'Personen', 'osobe')}
												</div>
												<div className="text-muted small mt-1">
													{copy(
														'to execute this plan in a realistic timeframe',
														'um diesen Plan in einem realistischen Zeitrahmen umzusetzen',
														'za izvedbu ovog plana u realnom vremenskom okviru'
													)}
												</div>
											</div>
										)}
										{displayedPlanAi?.required_skills && displayedPlanAi.required_skills.length > 0 && (
											<>
												{displayedPlanAi.recommended_team_size != null && displayedPlanAi.recommended_team_size >= 1 && <hr className="my-2" />}
												<div className="text-muted small mb-2">{copy('Skills needed', 'Benötigte Kompetenzen', 'Potrebne vještine')}</div>
												<div className="d-flex flex-wrap gap-2">
													{displayedPlanAi.required_skills.map((skill) => (
														<span key={skill} className="badge bg-success-subtle text-success-emphasis">
															{getSkillLabel(skill, language)}
														</span>
													))}
												</div>
											</>
										)}
									</div>
								</div>
							)}
							{/* Questionnaire Tab */}
							{activeTab === 'questionnaire' && (
								<>
									<div className="card mb-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}</h5>
										</div>
										<div className="card-body">
											{plan?.questionnaire ? (
												<>
													{/* Garden basics */}
													<div className="mb-4">
														<div className="d-flex flex-wrap align-items-center gap-2 mb-2">
															{plan.styleKey && (
																<span className="badge bg-primary text-white">
																	<i className="bi bi-palette me-1" /> {localizeStyleLabel(plan.styleKey)}
																</span>
															)}
															{plan.questionnaire.gardenShape && (
																<span className="badge bg-success text-white">
																	<i className="bi bi-vector-pen me-1" /> {plan.questionnaire.gardenShape}
																</span>
															)}
														</div>

														{plan.questionnaire.gardenSize > 0 && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Garden size', 'Gartengröße', 'Veličina vrta')}</div>
																<div className="fw-semibold">
																	{plan.questionnaire.gardenSize} m²
																</div>
															</div>
														)}

														{(plan.questionnaire.location || plan.city) && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Location', 'Standort', 'Lokacija')}</div>
																<div className="fw-semibold">{plan.questionnaire.location || plan.city}</div>
															</div>
														)}
														{plan.questionnaire.projectFocus && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Project focus', 'Projektfokus', 'Fokus projekta')}</div>
																<div className="fw-semibold">{localizeProjectFocusLabel(plan.questionnaire.projectFocus)}</div>
															</div>
														)}

														{plan.questionnaire.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
															<div className="mt-3">
																<div className="text-muted small mb-2">{copy('Garden components', 'Gartenkomponenten', 'Komponente vrta')}</div>
																<div className="d-flex flex-wrap gap-2">
																	{plan.questionnaire.selectedComponents.map((componentId) => (
																		<span key={componentId} className="badge bg-success text-white">
																			<i className="bi bi-check-circle me-1" />
																			{getComponentLabel(componentId, language)}
																		</span>
																	))}
																</div>
															</div>
														)}
													</div>

													{/* Intent + preferences */}
													<div className="mb-4">
														{plan.questionnaire.needsWants && plan.questionnaire.needsWants.trim().length > 0 && (
															<div className="mb-3">
																<div className="text-muted small mb-1">{copy('Needs & wants', 'Bedürfnisse & Wünsche', 'Potrebe i želje')}</div>
																<div className="text-break">{plan.questionnaire.needsWants}</div>
															</div>
														)}

														{plan.questionnaire.maintenanceLevel && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Maintenance level', 'Pflegeaufwand', 'Razina održavanja')}</div>
																<div className="fw-semibold text-capitalize">{plan.questionnaire.maintenanceLevel}</div>
															</div>
														)}

														{plan.questionnaire.plantDensityPreference && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Plant density', 'Pflanzdichte', 'Gustoća sadnje')}</div>
																<div className="fw-semibold text-capitalize">{plan.questionnaire.plantDensityPreference}</div>
															</div>
														)}

														{plan.questionnaire.gardenUses && plan.questionnaire.gardenUses.length > 0 && (
															<div className="mt-3">
																<div className="text-muted small mb-2">{copy('How you’ll use the garden', 'Wie Sie den Garten nutzen', 'Kako ćete koristiti vrt')}</div>
																<div className="d-flex flex-wrap gap-2">
																	{plan.questionnaire.gardenUses.map((useKey) => {
																		const labelMap: Record<string, string> = {
																			relaxing: 'Relaxing / quiet time',
																			growing_food: 'Growing food',
																			kids_pets: 'Kids & pets',
																			hosting: 'Hosting',
																			dining_outdoors: 'Outdoor dining'
																		};
																		return (
																			<span key={useKey} className="badge bg-secondary text-white">
																				{labelMap[useKey] ?? useKey}
																			</span>
																		);
																	})}
																</div>
															</div>
														)}
													</div>

													{/* Hosting + kids/pets */}
													<div className="mb-4">
														{plan.questionnaire.hostingMaxPeople != null && plan.questionnaire.hostingMaxPeople > 0 && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Hosting capacity', 'Kapazität für Gäste', 'Kapacitet za goste')}</div>
																<div className="fw-semibold">{plan.questionnaire.hostingMaxPeople} people</div>
															</div>
														)}

														{plan.questionnaire.childrenPets && plan.questionnaire.childrenPets !== 'none' && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Kids/pets priority', 'Priorität Kinder/Haustiere', 'Prioritet djece/ljubimaca')}</div>
																<div className="fw-semibold text-capitalize">{plan.questionnaire.childrenPets}</div>
															</div>
														)}

														{plan.questionnaire.kidsCount != null && plan.questionnaire.kidsCount > 0 && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Kids count', 'Anzahl Kinder', 'Broj djece')}</div>
																<div className="fw-semibold">{plan.questionnaire.kidsCount}</div>
															</div>
														)}

														{plan.questionnaire.kidsAges && plan.questionnaire.kidsAges.trim().length > 0 && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Kids ages', 'Alter der Kinder', 'Dob djece')}</div>
																<div className="fw-semibold">{plan.questionnaire.kidsAges}</div>
															</div>
														)}

														{plan.questionnaire.petsDescription && plan.questionnaire.petsDescription.trim().length > 0 && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Pets note', 'Hinweis zu Haustieren', 'Napomena o ljubimcima')}</div>
																<div className="text-break">{plan.questionnaire.petsDescription}</div>
															</div>
														)}
													</div>

													{/* Budget + timing */}
													<div className="mb-1">
														{plan.questionnaire.budgetRange && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Budget range', 'Budgetspanne', 'Raspon budžeta')}</div>
																<div className="fw-semibold">
																	€{plan.questionnaire.budgetRange.min.toLocaleString()} - €{plan.questionnaire.budgetRange.max.toLocaleString()}
																</div>
															</div>
														)}

														{plan.questionnaire.budgetPreference != null && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Budget preference', 'Budgetpräferenz', 'Preferencija budžeta')}</div>
																<div className="fw-semibold">{plan.questionnaire.budgetPreference}/100</div>
															</div>
														)}

														{plan.questionnaire.timeline && plan.questionnaire.timeline.trim().length > 0 && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</div>
																<div className="fw-semibold">{plan.questionnaire.timeline}</div>
															</div>
														)}

														{plan.questionnaire.projectStartDate && (
															<div className="mb-2">
																<div className="text-muted small">{copy('Preferred start date', 'Bevorzugtes Startdatum', 'Željeni datum početka')}</div>
																<div className="fw-semibold">
																	{new Date(plan.questionnaire.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
																</div>
															</div>
														)}
													</div>

													{/* Analysis feedback (user-provided) */}
													{plan.questionnaire.aiAnalysisComments && plan.questionnaire.aiAnalysisComments.trim().length > 0 && (
														<div className="mt-4">
															<div className="text-muted small mb-1">{copy('Photo analysis notes (your feedback)', 'Hinweise zur Fotoanalyse (Ihre Notizen)', 'Bilješke analize fotografije (vaše napomene)')}</div>
															<div className="text-break">{plan.questionnaire.aiAnalysisComments}</div>
														</div>
													)}
												</>
											) : (
												<p className="text-muted mb-0">{copy('No questionnaire data found for this plan.', 'Für diesen Plan wurden keine Fragebogendaten gefunden.', 'Za ovaj plan nisu pronađeni podaci upitnika.')}</p>
											)}
										</div>
									</div>

									{/* Smart Q&A (collapsed) */}
									<div className="card mb-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Smart question responses', 'Antworten auf intelligente Fragen', 'Odgovori na pametna pitanja')}</h5>
										</div>
										<div className="card-body">
											{plan?.questionnaire?.aiAnswers || plan?.questionnaire?.aiQuestionComments ? (
												<details>
													<summary style={{ cursor: 'pointer', fontWeight: 600 }}>
														{copy('Show answered Smart Questions', 'Beantwortete intelligente Fragen anzeigen', 'Prikaži odgovore na pametna pitanja')}
													</summary>
													<div className="mt-3">
														{(() => {
															const q = plan.questionnaire!;
															const aiAnswers = (q.aiAnswers ?? {}) as Record<string, any>;
															const aiQuestionComments = (q.aiQuestionComments ?? {}) as Record<string, string>;

															const answerKeys = Object.keys(aiAnswers)
																.filter((k) => /^ai_q_\d+$/.test(k))
																.filter((k) => {
																	const v = aiAnswers[k];
																	if (v == null) return false;
																	if (typeof v === 'string') return v.trim().length > 0;
																	if (Array.isArray(v)) return v.some((x) => String(x ?? '').trim().length > 0);
																	return true;
																});

															const indices = Array.from(
																new Set(
																	answerKeys
																		.map((k) => String(k).match(/^ai_q_(\d+)$/)?.[1])
																		.filter((s): s is string => typeof s === 'string' && s.length > 0)
																		.map((s) => parseInt(s, 10))
																)
															);

															// Also include indices that only have comments (edge case).
															for (const k of Object.keys(aiQuestionComments)) {
																const idx = parseInt(k, 10);
																if (!Number.isNaN(idx)) indices.push(idx);
															}

															const uniqueSorted = Array.from(new Set(indices)).sort((a, b) => a - b);

															if (uniqueSorted.length === 0) {
																return <p className="text-muted mb-0">{copy('No Smart Questions were answered.', 'Es wurden keine intelligenten Fragen beantwortet.', 'Nijedno pametno pitanje nije odgovoreno.')}</p>;
															}

															const suggested = q.aiAnalysis?.suggestedQuestions ?? [];

															const formatAnswer = (val: any): string => {
																if (val == null) return '';
																if (Array.isArray(val)) return val.filter((x) => String(x ?? '').trim().length > 0).map(String).join(', ');
																if (typeof val === 'boolean') return val ? 'Yes' : 'No';
																const str = String(val);
																if (str === 'yes') return 'Yes';
																if (str === 'no') return 'No';
																return str;
															};

															return (
																<div>
																	{uniqueSorted.map((idx) => {
																		const answerKey = `ai_q_${idx}`;
																		const answerVal = aiAnswers[answerKey];
																		const commentVal = aiQuestionComments[String(idx)];
																		const qText = suggested?.[idx]?.question;
																		const title = qText?.trim().length ? qText : copy('Smart question', 'Intelligente Frage', 'Pametno pitanje');
																		const formatted = formatAnswer(answerVal);

																		if (!formatted && (!commentVal || !commentVal.trim().length)) return null;

																		return (
																			<div key={idx} className="mb-3 pb-3 border-bottom">
																				<div className="fw-semibold mb-1">{title}</div>
																				{formatted && <div className="small text-muted">{copy('Answer:', 'Antwort:', 'Odgovor:')} {formatted}</div>}
																				{commentVal && commentVal.trim().length > 0 && (
																					<div className="small mt-1">
																						<i className="bi bi-chat-left-text me-1" />
																						<strong>{copy('Your note:', 'Ihre Notiz:', 'Vaša bilješka:')}</strong> <span className="text-break">{commentVal}</span>
																					</div>
																				)}
																			</div>
																		);
																	})}
																</div>
															);
														})()}
													</div>
												</details>
											) : (
												<p className="text-muted mb-0">{copy('No Smart Question data found.', 'Es wurden keine Daten zu intelligenten Fragen gefunden.', 'Nisu pronađeni podaci pametnih pitanja.')}</p>
											)}
										</div>
									</div>
								</>
							)}
						</div>

						{/* Sidebar - Actions */}
						<div className="col-12 col-lg-4">
							<div className="card">
								<div className="card-header">{copy('Actions', 'Aktionen', 'Akcije')}</div>
								<div className="card-body">
									{isGardenerMarketplaceViewer && canSendMarketplaceOffers && (
										<Link to={`/app/plans/${plan.id}`} className="btn btn-success w-100 mb-2">
											<i className="bi bi-send me-1"></i>{copy('Prepare offer', 'Angebot vorbereiten', 'Pripremi ponudu')}
										</Link>
									)}
									{user?.role === 'company' && !!plan.databaseId && hasAIContent && (
										<Link
											to={`/app/plans/${plan.id}`}
											state={{ returnTo: `/app/seeker/plan/${plan.id}` }}
											className="btn btn-success w-100 mb-2"
										>
											<i className="bi bi-receipt me-1"></i>
											{copy('Build quote', 'Angebot erstellen', 'Izradi ponudu')}
										</Link>
									)}
									{user?.role === 'company' && !!plan.databaseId && (
										<>
											<div className="border rounded p-2 mb-2 small">
												<div className="fw-semibold mb-1">{copy('Client invite status', 'Status der Kundeneinladung', 'Status pozivnice klijentu')}</div>
												<div className="text-muted mb-1">
													{!companyInviteStatus?.has_invite
														? copy('No invite sent', 'Keine Einladung gesendet', 'Pozivnica nije poslana')
														: companyInviteStatus.status === 'pending'
														? copy('Invite sent (waiting for acceptance)', 'Einladung gesendet (wartet auf Annahme)', 'Pozivnica poslana (čeka prihvat)')
														: companyInviteStatus.status === 'used'
														? `${copy('Accepted by', 'Angenommen von', 'Prihvatio/la')} ${companyInviteStatus.linked_user_name || companyInviteStatus.linked_user_email || copy('client', 'Kunde', 'klijent')}`
														: companyInviteStatus.status === 'cancelled'
														? copy('Invite cancelled', 'Einladung storniert', 'Pozivnica otkazana')
														: companyInviteStatus.status}
												</div>
												{companyInviteStatus?.has_invite && companyInviteStatus.code && (
													<div className="d-flex align-items-center gap-2 mb-1">
														<code>{companyInviteStatus.code}</code>
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															onClick={() => navigator.clipboard?.writeText(companyInviteStatus.code || '').catch(() => {})}
														>
															{copy('Copy', 'Kopieren', 'Kopiraj')}
														</button>
													</div>
												)}
												{companyInviteStatus?.has_invite && companyInviteStatus.status === 'pending' && (
													<button
														type="button"
														className="btn btn-sm btn-outline-danger"
														disabled={isCancellingCompanyInvite}
														onClick={async () => {
															if (!plan.databaseId || !user?.id) return;
															const userIdNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
															if (!userIdNum || Number.isNaN(userIdNum)) return;
															try {
																setIsCancellingCompanyInvite(true);
																await cancelCompanyInviteForPlan(plan.databaseId, userIdNum);
																const refreshed = await getCompanyInviteForPlan(plan.databaseId, userIdNum);
																setCompanyInviteStatus(refreshed);
																showAlert(copy('Invite cancelled.', 'Einladung storniert.', 'Pozivnica je otkazana.'), { title: copy('Invite updated', 'Einladung aktualisiert', 'Pozivnica ažurirana') });
															} catch (err) {
																showAlert(err instanceof Error ? err.message : copy('Could not cancel invite.', 'Einladung konnte nicht storniert werden.', 'Pozivnicu nije moguće otkazati.'), { title: copy('Cancel failed', 'Stornierung fehlgeschlagen', 'Otkazivanje nije uspjelo') });
															} finally {
																setIsCancellingCompanyInvite(false);
															}
														}}
													>
														{isCancellingCompanyInvite ? copy('Cancelling...', 'Wird storniert...', 'Otkazivanje...') : copy('Cancel invite', 'Einladung stornieren', 'Otkaži pozivnicu')}
													</button>
												)}
											</div>
											{companyInviteStatus?.has_invite &&
												inviteAllowsClientVisibilityEdit(companyInviteStatus.status) && (
													<>
														{companyInviteStatus.share_visibility && (
															<div className="text-muted small mb-2">
																<i className="bi bi-eye me-1" aria-hidden />
																{summarizeShareVisibility(
																	normalizeShareVisibility(companyInviteStatus.share_visibility),
																	copy
																)}
															</div>
														)}
														<button
															type="button"
															className="btn btn-outline-secondary w-100 mb-2"
															onClick={() => setClientVisibilityModalOpen(true)}
														>
															<i className="bi bi-eye me-1" />
															{copy('Client view settings', 'Kundenansicht', 'Postavke prikaza za klijenta')}
														</button>
													</>
												)}
											<button
												className="btn btn-outline-success w-100 mb-2"
												onClick={() => {
													setInviteModalOpen(true);
													setInviteCode(companyInviteStatus?.code || null);
													setInviteShareText(null);
													setInviteShareVisibility(
														normalizeShareVisibility(companyInviteStatus?.share_visibility)
													);
												}}
											>
												{companyInviteStatus?.has_invite && companyInviteStatus.status === 'pending' ? copy('Resend / Update Invite', 'Einladung erneut senden / aktualisieren', 'Ponovno pošalji / ažuriraj pozivnicu') : copy('Invite Client', 'Kunden einladen', 'Pozovi klijenta')}
											</button>
										</>
									)}
									{plan.status === 'active' && !isGardenerMarketplaceViewer && (
										<Link to={`/app/seeker/plan/${plan.id}/offers`} className="btn btn-primary w-100 mb-2">
											{copy('View Offers', 'Angebote ansehen', 'Pogledaj ponude')}
										</Link>
									)}
									{user?.role === 'seeker' && myInviteForPlan?.has_invite && myInviteForPlan.status === 'pending' && (
										<div className="alert alert-info small py-2 mb-2">
											<div className="fw-semibold mb-1">
												Invite from {myInviteForPlan.company_name || 'company'}
											</div>
											<div className="d-flex gap-2">
												<button
													className="btn btn-sm btn-success"
													disabled={isResolvingInvite}
													onClick={async () => {
														if (!user?.id || !myInviteForPlan.code) return;
														const userIdNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
														if (!userIdNum || Number.isNaN(userIdNum)) return;
														try {
															setIsResolvingInvite(true);
															await acceptPlanInviteForPlan(plan.databaseId!, userIdNum);
															const refreshed = await getMyInviteForPlan(plan.databaseId!, userIdNum);
															setMyInviteForPlan(refreshed);
															showAlert('Invite accepted. You can now continue with this plan.', { title: 'Invite accepted' });
														} catch (err) {
															showAlert(err instanceof Error ? err.message : 'Could not accept invite.', { title: 'Accept failed' });
														} finally {
															setIsResolvingInvite(false);
														}
													}}
												>
													Accept
												</button>
												<button
													className="btn btn-sm btn-outline-danger"
													disabled={isResolvingInvite}
													onClick={async () => {
														if (!user?.id || !myInviteForPlan.invite_id) return;
														const userIdNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
														if (!userIdNum || Number.isNaN(userIdNum)) return;
														try {
															setIsResolvingInvite(true);
															await declinePlanInvite(myInviteForPlan.invite_id, userIdNum);
															setMyInviteForPlan({ has_invite: false });
															showAlert(copy('Invite declined.', 'Einladung abgelehnt.', 'Poziv je odbijen.'), { title: copy('Invite declined', 'Einladung abgelehnt', 'Poziv odbijen') });
															navigate('/app/seeker/plans');
														} catch (err) {
															showAlert(err instanceof Error ? err.message : copy('Could not decline invite.', 'Einladung konnte nicht abgelehnt werden.', 'Poziv nije moguće odbiti.'), { title: copy('Decline failed', 'Ablehnung fehlgeschlagen', 'Odbijanje nije uspjelo') });
														} finally {
															setIsResolvingInvite(false);
														}
													}}
												>
													{copy('Reject', 'Ablehnen', 'Odbij')}
												</button>
											</div>
										</div>
									)}
									{plan.status === 'draft' && plan.questionnaire && !isGardenerMarketplaceViewer && (
										<button
											className="btn btn-outline-secondary w-100 mb-2"
											disabled={user?.role === 'seeker' && !!myInviteForPlan?.has_invite && myInviteForPlan.status !== 'used'}
											title={user?.role === 'seeker' && !!myInviteForPlan?.has_invite && myInviteForPlan.status !== 'used' ? copy('Accept the invite before editing this draft.', 'Akzeptieren Sie die Einladung, bevor Sie diesen Entwurf bearbeiten.', 'Prihvatite poziv prije uređivanja ove skice.') : undefined}
											onClick={() => {
												localStorage.setItem('editingPlanId', plan.id);
												localStorage.removeItem('gardenQuestionnaireDraft');
												navigate('/app/seeker/create-plan');
											}}
										>
											{copy('Edit Questionnaire', 'Fragebogen bearbeiten', 'Uredi upitnik')}
										</button>
									)}
									{plan.status === 'draft' && !isGardenerMarketplaceViewer && (
										<button 
											className="btn btn-success w-100" 
											disabled={user?.role === 'seeker' && !!myInviteForPlan?.has_invite && myInviteForPlan.status !== 'used'}
											title={user?.role === 'seeker' && !!myInviteForPlan?.has_invite && myInviteForPlan.status !== 'used' ? copy('Accept the invite before publishing this draft.', 'Akzeptieren Sie die Einladung, bevor Sie diesen Entwurf objavljujete.', 'Prihvatite poziv prije objave ove skice.') : undefined}
											onClick={async () => {
												try {
													const hasGeneratedPlan =
														!!plan.questionnaire &&
														!!plan.aiGenerated &&
														Array.isArray(plan.aiGenerated.instructions) &&
														plan.aiGenerated.instructions.length > 0;
													if (!hasGeneratedPlan) {
														showAlert(
															copy('This draft does not have a generated plan yet. Open Edit, finish the questionnaire, and generate the plan first.', 'Dieser Entwurf hat noch keinen generierten Plan. Öffnen Sie Bearbeiten, schließen Sie den Fragebogen ab und generieren Sie zuerst den Plan.', 'Ova skica još nema generirani plan. Otvorite uređivanje, dovršite upitnik i prvo generirajte plan.'),
															{ title: copy('Plan not ready to publish', 'Plan ist noch nicht bereit zur Veröffentlichung', 'Plan još nije spreman za objavu') }
														);
														return;
													}
													if (plan.databaseId) {
														await updatePlanStatus(plan.databaseId, 'published');
														const updated = await getPublicPlanByIdFromAPI(plan.id.startsWith('db-') ? plan.id : `db-${plan.databaseId}`);
														if (updated) {
															setPlan(updated);
															showAlert(copy('Plan published! Gardeners can now see it and send offers.', 'Plan veröffentlicht! Gärtner können ihn jetzt sehen und Angebote senden.', 'Plan je objavljen! Vrtlari ga sada mogu vidjeti i slati ponude.'), { title: copy('Plan published', 'Plan veröffentlicht', 'Plan objavljen') });
														} else {
															setPlan({ ...plan, status: 'active' });
															showAlert(copy('Plan published! Gardeners can now see it and send offers.', 'Plan veröffentlicht! Gärtner können ihn jetzt sehen und Angebote senden.', 'Plan je objavljen! Vrtlari ga sada mogu vidjeti i slati ponude.'), { title: copy('Plan published', 'Plan veröffentlicht', 'Plan objavljen') });
														}
													} else {
														const planToUpdate = getPublicPlans().find(p => p.id === plan.id || p.databaseId === plan.databaseId);
														const idToUpdate = planToUpdate?.id ?? plan.id;
														updatePublicPlan(idToUpdate, { status: 'active' });
														const updated = getPublicPlans().find(p => p.id === idToUpdate);
														if (updated) {
															setPlan(updated);
															showAlert(copy('Plan published! Gardeners can now see it and send offers.', 'Plan veröffentlicht! Gärtner können ihn jetzt sehen und Angebote senden.', 'Plan je objavljen! Vrtlari ga sada mogu vidjeti i slati ponude.'), { title: copy('Plan published', 'Plan veröffentlicht', 'Plan objavljen') });
														} else {
															showAlert(copy('Could not publish. Try refreshing the page.', 'Veröffentlichung nicht möglich. Bitte Seite aktualisieren und erneut versuchen.', 'Objava nije uspjela. Pokušajte osvježiti stranicu.'), { title: copy('Publishing failed', 'Veröffentlichung fehlgeschlagen', 'Objava nije uspjela') });
														}
													}
												} catch (err) {
													console.error('Failed to publish draft plan', err);
													showAlert(err instanceof Error ? err.message : copy('Publishing failed. Try again.', 'Veröffentlichung fehlgeschlagen. Bitte erneut versuchen.', 'Objava nije uspjela. Pokušajte ponovno.'), { title: copy('Publishing failed', 'Veröffentlichung fehlgeschlagen', 'Objava nije uspjela') });
												}
											}}
										>
											{copy('Publish Plan', 'Plan veröffentlichen', 'Objavi plan')}
										</button>
									)}
									{hasAIContent && (
										<button className="btn btn-outline-primary w-100" onClick={handleDownloadPDF}>
											{copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}
										</button>
									)}
									{user?.role !== 'seeker' && (
										<button
											type="button"
											className="btn btn-outline-danger btn-sm w-100 mt-2"
											onClick={handleReportSeekerFromPlan}
											disabled={isReportingUser}
										>
											<i className="bi bi-flag me-1"></i>
											{isReportingUser ? copy('Sending...', 'Senden...', 'Slanje...') : copy('Report user', 'Nutzer melden', 'Prijavi korisnika')}
										</button>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="alert alert-warning border-warning-subtle mt-2 mb-0">
						<div className="d-flex">
							<i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
							<div className="small">
								<strong>{copy('Note:', 'Hinweis:', 'Napomena:')}</strong>{' '}
								{copy(
									'this plan is prepared by our team using software-assisted planning tools. It is an early estimate, and final scope, timeline, team size, and pricing are confirmed in the accepted company offer.',
									'dieser Plan wird von unserem Team mithilfe softwaregestützter Planungstools erstellt. Es handelt sich um eine frühe Schätzung; endgültiger Umfang, Zeitplan, Teamgröße und Preis werden im angenommenen Firmenangebot bestätigt.',
									'ovaj plan priprema naš tim uz pomoć softverskih alata za planiranje. Ovo je rana procjena, a konačni opseg, vremenski plan, veličina tima i cijena potvrđuju se u prihvaćenoj ponudi tvrtke.'
								)}
							</div>
						</div>
					</div>
				</>
			) : (
				// Fallback when we do not have both questionnaire data and AI output (see hasAIContent)
				<div className="row g-3">
					<div className="col-12 col-lg-8">
						<div className="card">
							<div className="card-header">
								<h5 className="mb-0">{copy('Plan Details', 'Plandetails', 'Detalji plana')}</h5>
							</div>
							{(() => {
								const beforeUrl = plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl;
								if (beforeUrl) {
									const src = getImageUrl(beforeUrl);
									return (
										<img
											src={src || beforeUrl}
											className="card-img-top"
											alt={copy('Garden before photo', 'Gartenfoto (Vorher)', 'Fotografija vrta (prije)')}
											style={{ maxHeight: 420, objectFit: 'cover', width: '100%' }}
											onError={(e) => {
												const el = e.currentTarget;
												el.onerror = null;
												el.src = PLAN_CARD_FALLBACK_IMAGE;
												el.style.objectFit = 'cover';
											}}
										/>
									);
								}
								return (
									<div className="border-bottom bg-light text-center py-5 px-3 text-muted small">
										{copy(
											'No garden photo uploaded yet. You can add one when you continue the questionnaire.',
											'Noch kein Gartenfoto hochgeladen. Sie können eines hinzufügen, wenn Sie den Fragebogen fortsetzen.',
											'Još nema fotografije vrta. Možete je dodati kad nastavite upitnik.'
										)}
									</div>
								);
							})()}
							<div className="card-body">
								<h4 className="h5 mb-3">{plan.title}</h4>
								<div className="row g-3 mb-3">
									<div className="col-6">
										<div className="text-muted small mb-1">{copy('City', 'Stadt', 'Grad')}</div>
										<div className="fw-medium">{plan.city || copy('—', '—', '—')}</div>
									</div>
									<div className="col-6">
										<div className="text-muted small mb-1">{copy('Style', 'Stil', 'Stil')}</div>
										<div className="fw-medium">{localizeStyleLabel(plan.styleKey)}</div>
									</div>
									{plan.questionnaire?.gardenSize != null && plan.questionnaire.gardenSize > 0 && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Garden size', 'Gartengröße', 'Veličina vrta')}</div>
											<div className="fw-medium">{plan.questionnaire.gardenSize} m²</div>
										</div>
									)}
									{plan.questionnaire?.budgetRange && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Budget range', 'Budgetspanne', 'Raspon proračuna')}</div>
											<div className="fw-medium">
												{plan.questionnaire.budgetRange.min?.toLocaleString()} – {plan.questionnaire.budgetRange.max?.toLocaleString()} EUR
											</div>
										</div>
									)}
									{plan.questionnaire?.gardenShape && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Garden shape', 'Gartenform', 'Oblik vrta')}</div>
											<div className="fw-medium text-capitalize">{plan.questionnaire.gardenShape.replace(/-/g, ' ')}</div>
										</div>
									)}
									{plan.questionnaire?.timeline?.trim() && (
										<div className="col-12 col-sm-6">
											<div className="text-muted small mb-1">{copy('Timeline', 'Zeitrahmen', 'Vremenski okvir')}</div>
											<div className="fw-medium">{plan.questionnaire.timeline}</div>
										</div>
									)}
									{plan.questionnaire?.projectStartDate && (
										<div className="col-12 col-sm-6">
											<div className="text-muted small mb-1">{copy('Preferred start', 'Gewünschter Start', 'Željeni početak')}</div>
											<div className="fw-medium">{new Date(plan.questionnaire.projectStartDate).toLocaleDateString(locale)}</div>
										</div>
									)}
									{plan.questionnaire?.maintenanceLevel && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Maintenance level', 'Pflegeaufwand', 'Razina održavanja')}</div>
											<div className="fw-medium text-capitalize">{plan.questionnaire.maintenanceLevel}</div>
										</div>
									)}
									{plan.questionnaire?.plantDensityPreference && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Plant density', 'Pflanzdichte', 'Gustoća sadnje')}</div>
											<div className="fw-medium text-capitalize">{plan.questionnaire.plantDensityPreference}</div>
										</div>
									)}
									{plan.questionnaire?.projectFocus && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Project focus', 'Projektfokus', 'Fokus projekta')}</div>
											<div className="fw-medium">{localizeProjectFocusLabel(plan.questionnaire.projectFocus)}</div>
										</div>
									)}
									{plan.questionnaire?.householdSize != null && plan.questionnaire.householdSize > 0 && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Household size', 'Haushaltsgröße', 'Veličina kućanstva')}</div>
											<div className="fw-medium">{plan.questionnaire.householdSize}</div>
										</div>
									)}
									{plan.questionnaire?.gardenUsers != null && plan.questionnaire.gardenUsers > 0 && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Garden users', 'Gartennutzer', 'Vlasnici vrta')}</div>
											<div className="fw-medium">{plan.questionnaire.gardenUsers}</div>
										</div>
									)}
									{plan.questionnaire?.hostingMaxPeople != null && plan.questionnaire.hostingMaxPeople > 0 && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Hosting capacity', 'Kapazität für Gäste', 'Kapacitet za goste')}</div>
											<div className="fw-medium">{plan.questionnaire.hostingMaxPeople}</div>
										</div>
									)}
									{plan.questionnaire?.childrenPets && plan.questionnaire.childrenPets !== 'none' && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Kids / pets', 'Kinder / Haustiere', 'Djeca / ljubimci')}</div>
											<div className="fw-medium text-capitalize">{plan.questionnaire.childrenPets}</div>
										</div>
									)}
									{plan.questionnaire?.kidsCount != null && plan.questionnaire.kidsCount > 0 && (
										<div className="col-6">
											<div className="text-muted small mb-1">{copy('Kids count', 'Anzahl Kinder', 'Broj djece')}</div>
											<div className="fw-medium">{plan.questionnaire.kidsCount}</div>
										</div>
									)}
									{plan.questionnaire?.kidsAges?.trim() && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Kids ages', 'Alter der Kinder', 'Dob djece')}</div>
											<div className="fw-medium">{plan.questionnaire.kidsAges}</div>
										</div>
									)}
									{plan.questionnaire?.petsDescription?.trim() && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Pets', 'Haustiere', 'Ljubimci')}</div>
											<div className="fw-medium">{plan.questionnaire.petsDescription}</div>
										</div>
									)}
								</div>
								{plan.questionnaire?.gardenUses && plan.questionnaire.gardenUses.length > 0 && (
									<div className="mb-3">
										<div className="text-muted small mb-2">{copy('How you’ll use the garden', 'Wie Sie den Garten nutzen', 'Kako ćete koristiti vrt')}</div>
										<div className="d-flex flex-wrap gap-2">
											{plan.questionnaire.gardenUses.map((useKey) => {
												const label =
													useKey === 'relaxing'
														? copy('Relaxing / quiet time', 'Entspannung / Ruhe', 'Odmor / tišina')
														: useKey === 'growing_food'
															? copy('Growing food', 'Eigenes Obst/Gemüse', 'Uzgoj hrane')
															: useKey === 'kids_pets'
																? copy('Kids & pets', 'Kinder & Haustiere', 'Djeca i ljubimci')
																: useKey === 'hosting'
																	? copy('Hosting', 'Gäste empfangen', 'Gostovanje')
																	: useKey === 'dining_outdoors'
																		? copy('Outdoor dining', 'Essen im Freien', 'Obrok na otvorenom')
																		: useKey;
												return (
													<span key={useKey} className="badge bg-secondary">
														{label}
													</span>
												);
											})}
										</div>
									</div>
								)}
								{plan.questionnaire?.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
									<div className="mb-3">
										<div className="text-muted small mb-2">{copy('Wish list', 'Wunschliste', 'Lista želja')}</div>
										<ul className="mb-0 ps-3">
											{plan.questionnaire.selectedComponents.map((id) => (
												<li key={id}>{getComponentLabel(id, language)}</li>
											))}
										</ul>
									</div>
								)}
								{plan.notes && (
									<div className="mb-3">
										<div className="text-muted small mb-1">{copy('Description', 'Beschreibung', 'Opis')}</div>
										<p className="mb-0 text-break">{plan.notes}</p>
									</div>
								)}
								{plan.questionnaire?.needsWants?.trim() && (
									<div className="mb-3">
										<div className="text-muted small mb-1">{copy('Needs & wants', 'Bedürfnisse & Wünsche', 'Potrebe i želje')}</div>
										<p className="mb-0 text-break">{plan.questionnaire.needsWants}</p>
									</div>
								)}
								{!plan.aiGenerated && (
									<div className="alert alert-info mb-0">
										<i className="bi bi-info-circle me-2"></i>
										{user?.role === 'seeker'
											? copy(
													'This draft does not include a generated plan yet. Open “Edit draft” to finish the questionnaire and generate it.',
													'Dieser Entwurf enthält noch keinen generierten Plan. Öffnen Sie „Entwurf bearbeiten“, um den Fragebogen abzuschließen und zu generieren.',
													'Ova skica još nema generirani plan. Otvorite „Uredi skicu“ kako biste dovršili upitnik i generirali plan.'
												)
											: copy(
													'This plan does not have generated details yet. Contact the seeker if you need more information.',
													'Dieser Plan hat noch keine generierten Details. Kontaktieren Sie die suchende Person bei Bedarf.',
													'Ovaj plan još nema generirane detalje. Kontaktirajte vlasnika ako trebate više informacija.'
												)}
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-12 col-lg-4">
						<div className="card h-100">
							<div className="card-header">{copy('Actions', 'Aktionen', 'Akcije')}</div>
							<div className="card-body">
								{isGardenerMarketplaceViewer && canSendMarketplaceOffers && (
									<Link to={`/app/plans/${plan.id}`} className="btn btn-success btn-sm w-100 mb-2">
										Prepare offer
									</Link>
								)}
								{user?.role === 'company' && !!plan.databaseId && hasAIContent && (
									<Link
										to={`/app/plans/${plan.id}`}
										state={{ returnTo: `/app/seeker/plan/${plan.id}` }}
										className="btn btn-success btn-sm w-100 mb-2"
									>
										<i className="bi bi-receipt me-1"></i>
										{copy('Build quote', 'Angebot erstellen', 'Izradi ponudu')}
									</Link>
								)}
								{plan.status === 'active' && !isGardenerMarketplaceViewer && (
									<Link to={`/app/seeker/plan/${plan.id}/offers`} className="btn btn-outline-primary btn-sm w-100 mb-2">
										View Offers
									</Link>
								)}
								{plan.status === 'draft' && !isGardenerMarketplaceViewer && (
									<button
										className="btn btn-success btn-sm w-100"
										onClick={() => {
											localStorage.setItem('editingPlanId', plan.id);
											localStorage.removeItem('gardenQuestionnaireDraft');
											navigate('/app/seeker/create-plan');
										}}
									>
										Edit Draft
									</button>
								)}
								{user?.role !== 'seeker' && (
									<button
										type="button"
										className="btn btn-outline-danger btn-sm w-100 mt-2"
										onClick={handleReportSeekerFromPlan}
										disabled={isReportingUser}
									>
										<i className="bi bi-flag me-1"></i>
										{isReportingUser ? copy('Sending...', 'Senden...', 'Slanje...') : copy('Report user', 'Nutzer melden', 'Prijavi korisnika')}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Image Modal */}
			{plan && (() => {
				const afterImageUrl = displayedAiContent?.photoAfterUrl ?? plan.aiGenerated?.photoAfterUrl;
				const isPlaceholder = afterImageUrl?.includes('unsplash.com');
				const agForModal = displayedAiContent ?? plan.aiGenerated;
				const hasAfterImage =
					Boolean(afterImageUrl) &&
					!isPlaceholder &&
					(plan.status !== 'draft' || aiGeneratedHasDesignContent(agForModal));
				const beforeImageUrl = plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl;
				
				const images: Array<{ url: string; alt: string; label: string }> = [];
				if (beforeImageUrl) {
					const url = getImageUrl(beforeImageUrl) || beforeImageUrl;
					images.push({
						url,
						alt: 'Current State',
						label: 'Current State'
					});
				}
				if (hasAfterImage && afterImageUrl) {
					const url = getImageUrl(afterImageUrl) || afterImageUrl;
					images.push({
						url,
						alt: 'Design vision',
						label: 'Design vision'
					});
				}
				
				return (
					<ImageModal
						isOpen={imageModalOpen}
						onClose={() => setImageModalOpen(false)}
						images={images}
						initialIndex={imageModalIndex}
					/>
				);
			})()}

			{/* Regenerate with changes modal */}
			{inviteModalOpen && (
				<div
					className="modal d-block"
					style={{
						backgroundColor: 'rgba(0,0,0,0.45)',
						position: 'fixed',
						inset: 0,
						zIndex: 20000,
						overflowY: 'auto'
					}}
					aria-modal="true"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									{copy('Invite client to this plan', 'Kunden zu diesem Plan einladen', 'Pozovi klijenta na ovaj plan')}
								</h5>
								<button
									type="button"
									className="btn-close"
									onClick={() => !isInvitingClient && setInviteModalOpen(false)}
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
								/>
							</div>
							<div className="modal-body">
								<p className="text-muted small mb-2">
									{copy(
										"Create a shareable link for this exact plan. If email is provided and exists in the app, it appears directly in that client's My Plans.",
										'Erstellen Sie einen teilbaren Link für genau diesen Plan. Wenn eine E-Mail angegeben wird und im System existiert, erscheint der Plan direkt in den „Meine Pläne“ des Kunden.',
										'Izradite poveznicu za dijeljenje baš za ovaj plan. Ako je e-mail upisan i već postoji u aplikaciji, plan će se prikazati izravno u klijentovim Mojim planovima.'
									)}
								</p>
								<div className="mb-2">
									<label className="form-label mb-1">
										{copy('Client email (optional)', 'Kunden-E-Mail (optional)', 'E-mail klijenta (neobavezno)')}
									</label>
									<input
										type="email"
										className="form-control"
										placeholder="client@email.com"
										value={inviteEmail}
										onChange={(e) => setInviteEmail(e.target.value)}
										disabled={isInvitingClient || !!inviteShareText}
									/>
								</div>
								<div className="form-check mb-2">
									<input
										id="invite-allow-edit"
										type="checkbox"
										className="form-check-input"
										checked={inviteAllowEdit}
										onChange={(e) => setInviteAllowEdit(e.target.checked)}
										disabled={isInvitingClient || !!inviteShareText}
									/>
									<label htmlFor="invite-allow-edit" className="form-check-label">
										{copy('Allow client to edit this draft', 'Dem Kunden erlauben, diesen Entwurf zu bearbeiten', 'Dopusti klijentu uređivanje ovog nacrta')}
									</label>
								</div>
								<ShareVisibilitySettings
									value={inviteShareVisibility}
									onChange={setInviteShareVisibility}
									copy={copy}
									disabled={isInvitingClient || !!inviteShareText}
									compact
								/>
								{inviteCode && inviteShareText && (
									<InviteSharePanel inviteCode={inviteCode} shareText={inviteShareText} copy={copy} />
								)}
							</div>
							<div className="modal-footer">
								<button
									type="button"
									className={`btn ${inviteShareText ? 'btn-success' : 'btn-outline-secondary'}`}
									onClick={() => setInviteModalOpen(false)}
									disabled={isInvitingClient}
								>
									{copy('Close', 'Schließen', 'Zatvori')}
								</button>
								{!inviteShareText && (
								<button
									type="button"
									className="btn btn-success"
									disabled={isInvitingClient || !plan?.databaseId}
									onClick={async () => {
										if (!plan?.databaseId) return;
										try {
													const rawUserId = user?.id;
													const userIdNum =
														typeof rawUserId === 'number' ? rawUserId : parseInt(String(rawUserId ?? ''), 10);
													if (!userIdNum || Number.isNaN(userIdNum)) {
														showAlert(
															copy(
																'Your user account is missing a numeric ID.',
																'Ihrem Benutzerkonto fehlt eine numerische ID.',
																'Vašem korisničkom računu nedostaje brojčani ID.'
															),
															{ title: copy('Invite failed', 'Einladung fehlgeschlagen', 'Slanje pozivnice nije uspjelo') }
														);
														return;
													}
											setIsInvitingClient(true);
											const res = await inviteClientToPlan({
												planId: plan.databaseId,
														userId: userIdNum,
												invitedEmail: inviteEmail.trim() || undefined,
												allowEdit: inviteAllowEdit,
												shareVisibility: inviteShareVisibility
											});
											const code = res.code || null;
											setInviteCode(code);
											if (code) {
												const inviteUrl = buildAppUrl(`?invite=${encodeURIComponent(code)}`);
												const shareText = `Hey! 🌿\n\nI shared a garden plan with you on HortEra:\n${inviteUrl}\n\nLog in or sign up through this link to view it.`;
												setInviteShareText(shareText);
											}
											showAlert('Invite created for this plan.', { title: 'Invite ready' });
											const refreshed = await getCompanyInviteForPlan(plan.databaseId, userIdNum);
											setCompanyInviteStatus(refreshed);
										} catch (err) {
											console.error('Failed to invite client to plan', err);
											showAlert(
												err instanceof Error
													? err.message
													: copy('Failed to create invite.', 'Einladung konnte nicht erstellt werden.', 'Kreiranje pozivnice nije uspjelo.'),
												{ title: copy('Invite failed', 'Einladung fehlgeschlagen', 'Slanje pozivnice nije uspjelo') }
											);
										} finally {
											setIsInvitingClient(false);
										}
									}}
								>
									{isInvitingClient ? (
										<><span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Creating...</>
									) : (
										copy('Create invite link', 'Einladungslink erstellen', 'Stvori poveznicu pozivnice')
									)}
								</button>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Regenerate with changes modal */}
			{editModalOpen && (
				<div
					className="modal d-block"
					style={{
						backgroundColor: 'rgba(0,0,0,0.5)',
						position: 'fixed',
						inset: 0,
						zIndex: 20000,
						overflowY: 'auto'
					}}
					aria-modal="true"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{copy('Change something?', 'Etwas ändern?', 'Želite li nešto promijeniti?')}</h5>
								<button type="button" className="btn-close" onClick={() => !isRegenerating && setEditModalOpen(false)} aria-label="Close" />
							</div>
							{isRegenerating ? (
								<div className="modal-body py-4">
									<PlanGenerationPuzzleAnimation
										beforeImageUrl={regenerateAnimationBackgroundUrl}
										selectedComponents={plan.questionnaire?.selectedComponents}
										subMessage={copy("We're applying your requested changes and regenerating your plan. This usually takes about 1-3 minutes.", 'Wir setzen Ihre gewünschten Änderungen um und erstellen Ihren Plan neu. Dies dauert normalerweise 1-3 Minuten.', 'Primjenjujemo vaše tražene izmjene i ponovno generiramo vaš plan. To obično traje 1-3 minute.')}
									/>
								</div>
							) : (
								<>
									<div className="modal-body">
										<p className="text-muted small mb-2">
											{copy("Describe what you'd like to change. We'll regenerate the image and plan to match-from small tweaks (e.g. fence colour) to bigger changes (e.g. different style).", 'Beschreiben Sie, was Sie ändern möchten. Wir erstellen Bild und Plan passend dazu neu - von kleinen Anpassungen (z. B. Zaunfarbe) bis zu größeren Änderungen (z. B. anderer Stil).', 'Opišite što želite promijeniti. Ponovno ćemo izraditi sliku i plan prema tome - od manjih izmjena (npr. boja ograde) do većih promjena (npr. drugačiji stil).')}
										</p>
										<textarea
											className="form-control"
											rows={3}
											placeholder={copy('e.g. different colour fence, more flowers, make it more modern, less lawn...', 'z. B. andere Zaunfarbe, mehr Blumen, modernerer Stil, weniger Rasen...', 'npr. druga boja ograde, više cvijeća, moderniji stil, manje travnjaka...')}
											value={editInstructions}
											onChange={(e) => setEditInstructions(e.target.value)}
											disabled={isRegenerating}
										/>
									</div>
									<div className="modal-footer">
										<button type="button" className="btn btn-secondary" onClick={() => setEditModalOpen(false)} disabled={isRegenerating}>
											{copy('Cancel', 'Abbrechen', 'Odustani')}
										</button>
										<button type="button" className="btn btn-success" onClick={handleRegenerateWithChanges} disabled={isRegenerating || !editInstructions.trim()}>
											<>{copy('Regenerate', 'Neu erstellen', 'Ponovno izradi')} <i className="bi bi-arrow-repeat ms-1"></i></>
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{clientVisibilityModalOpen && plan?.databaseId && companyUserId && (
				<ClientVisibilityModal
					open
					onClose={() => setClientVisibilityModalOpen(false)}
					planDatabaseId={plan.databaseId}
					companyUserId={companyUserId}
					copy={copy}
					initialVisibility={companyInviteStatus?.share_visibility}
					onSaved={(saved) => {
						setCompanyInviteStatus((prev) => (prev ? { ...prev, share_visibility: saved } : prev));
						setPlan((prev) => (prev ? { ...prev, share_visibility: saved } : prev));
						showAlert(
							copy(
								'Client view updated. Your client will see the new settings immediately.',
								'Kundenansicht aktualisiert. Ihr Kunde sieht die neuen Einstellungen sofort.',
								'Prikaz za klijenta je ažuriran. Klijent će odmah vidjeti nove postavke.'
							),
							{ title: copy('Saved', 'Gespeichert', 'Spremljeno') }
						);
					}}
				/>
			)}
		</>
	);
}


